import { apiClient } from '@services/api';
import { getTurnstileToken, getTurnstileStatus, prefetchTurnstile, getDeviceInfo } from '@utils/antifraud';
import CircularProgress from '@material-ui/core/CircularProgress';
import { format } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import NumberFormat from 'react-number-format';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { makeT, presaleDict } from './presaleI18n';

// ─── Paleta ─────────────────────────────────────────────────────────────────

const C = {
  pageBg:       '#0a1628',
  navBg:        '#0f2040',
  card:         '#132035',
  cardInner:    '#0f1c2e',
  accent:       '#62DEC2',
  accentDim:    '#3aa38a',
  accentGold:   '#F5C842',
  danger:       '#e05252',
  label:        '#6e8eb0',
  value:        '#dce9fb',
  muted:        '#4a6380',
  sep:          '#1a3050',
  include:      '#2a5c42',
  notInclude:   '#5c2a2a',
  bubbleClient: '#1a3a5a',
  bubbleAgent:  '#1c3d2f',
  approveGrad:  'linear-gradient(135deg, #62DEC2 0%, #3aa38a 100%)',
  refuseGrad:   'linear-gradient(135deg, #e05252 0%, #b03030 100%)',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

// % de desconto Pix/Cartão por região. `promo` (Black Friday nov/2026) vem do backend
// (payment-settings.promo / voucher.bf_promo): quando existe, Santiago e Atacama usam
// promo.pix_pct / promo.card_pct. Sem promo → regra normal (12/10 Santiago e Atacama, 7/5 demais).
// Transfers nunca têm desconto.
const HIGH_REGIONS_LIST = ['santiago', 'atacama'];
const regionPcts = (region, isTransfer, promo) => {
  if (isTransfer) return { pix: 0, card: 0 };
  const high = HIGH_REGIONS_LIST.includes((region || '').toLowerCase().trim());
  if (promo && high) return { pix: Number(promo.pix_pct), card: Number(promo.card_pct) };
  return { pix: high ? 12 : 7, card: high ? 10 : 5 };
};

const fmtMoney = (value, style = {}) => (
  <NumberFormat
    value={value ?? 0}
    displayType="text"
    decimalSeparator=","
    decimalScale={2}
    fixedDecimalScale
    thousandSeparator="."
    isNumericString
    prefix={`${process.env.REACT_APP_CURRENCY || 'R$'} `}
    renderText={(v) => <span style={style}>{v}</span>}
  />
);

const fmtDate = (str) => {
  if (!str) return '—';
  // Força horário local para evitar deslocamento de fuso (UTC vs local)
  try {
    const d = str.length === 10 ? new Date(str + 'T00:00:00') : new Date(str);
    return format(d, 'dd/MM/yyyy');
  } catch (e) { return str; }
};

const fmtDateTime = (str) => {
  if (!str) return '—';
  try { return format(new Date(str), 'dd/MM/yyyy HH:mm'); } catch (e) { return str; }
};

const isExpired = (exp) => exp && new Date(exp) < new Date();

const imgUrl = (path) => {
  const base = process.env.REACT_APP_BASE_IMAGE_URL;
  if (!path || !base || base === 'undefined') return null;
  return `${base}/${path}`;
};

// ─── Sub-componentes ─────────────────────────────────────────────────────────

const Divider = () => (
  <div style={{ borderBottom: `1px solid ${C.sep}`, margin: '14px 0' }} />
);

const Label = ({ children }) => (
  <span style={{ color: C.label, fontSize: 12, display: 'block', marginBottom: 3 }}>
    {children}
  </span>
);

const Value = ({ children, style }) => (
  <span style={{ color: C.value, fontSize: 14, ...style }}>{children}</span>
);

const Row = ({ label, value, valueStyle, last }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingBottom: last ? 0 : 12,
    marginBottom: last ? 0 : 12,
    borderBottom: last ? 'none' : `1px solid ${C.sep}`,
    gap: 8,
  }}>
    <Label>{label}</Label>
    <span style={{ textAlign: 'right', color: C.value, ...valueStyle }}>{value}</span>
  </div>
);

const Badge = ({ color, bg, children }) => (
  <span style={{
    background: bg || color,
    color: '#fff',
    borderRadius: 20,
    padding: '4px 12px',
    fontSize: 12,
    fontWeight: 700,
    display: 'inline-block',
    letterSpacing: 0.3,
  }}>
    {children}
  </span>
);

const SectionTitle = ({ children, icon }) => (
  <div style={{
    color: C.accent,
    fontWeight: 700,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 1.5,
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  }}>
    {icon && <span>{icon}</span>}
    {children}
  </div>
);

const Card = ({ children, style }) => (
  <div style={{
    background: C.card,
    borderRadius: 16,
    padding: '20px 20px',
    marginBottom: 16,
    boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
    ...style,
  }}>
    {children}
  </div>
);

// ─── Seletor de data para mudança ────────────────────────────────────────────

const DateRequestSection = ({ tourId, tourDate: initialTourDate, presaleId, accessToken, budgetId, tourName }) => {
  const t = window.__ftPresaleT || ((k) => k);
  const [open, setOpen]           = React.useState(true);
  const [availDates, setAvailDates] = React.useState(null);
  const [viewYear, setViewYear]   = React.useState(() => {
    if (initialTourDate) { try { return new Date(`${initialTourDate}T00:00:00`).getFullYear(); } catch (e) {} }
    return new Date().getFullYear();
  });
  const [viewMonth, setViewMonth] = React.useState(() => {
    if (initialTourDate) { try { return new Date(`${initialTourDate}T00:00:00`).getMonth(); } catch (e) {} }
    return new Date().getMonth();
  });
  const [selDate, setSelDate]     = React.useState(null);
  const [saving, setSaving]       = React.useState(false);
  const [savedDate, setSavedDate] = React.useState(initialTourDate || null);

  React.useEffect(() => {
    if (!open || !tourId) return;
    setAvailDates(null);
    apiClient.httpInstance
      .get(`/app/tours/${tourId}/availability`)
      .then(r => {
        const map = {};
        (r.data?.dates || []).forEach(d => { map[d.date] = d.spots_left; });
        setAvailDates(map);
      })
      .catch(() => setAvailDates({}));
  }, [open, tourId]);

  const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
  const firstDayOfMonth = (y, m) => new Date(y, m, 1).getDay();

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const pad = n => String(n).padStart(2, '0');
  const toKey = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

  const MONTH_NAMES = t('months');
  const DAY_NAMES   = t('weekdays');

  const handleDayClick = (key, available) => {
    if (!available) return;
    setSelDate(key);
  };

  const handleSave = () => {
    if (!selDate || saving) return;
    setSaving(true);
    apiClient.httpInstance
      .put(`/customer/presales/${presaleId}/budgets/${budgetId}/date?access_token=${accessToken}`, { date: selDate })
      .then(() => {
        window.location.reload();
      })
      .catch(() => setSaving(false));
  };

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 8,
      }}>
        <span style={{ color: C.label, fontSize: 13, fontWeight: 600 }}>
          📅 {savedDate ? `${t('preferred_date')} ${fmtDate(savedDate)}` : t('choose_date_for_tour')}
        </span>
        <button
          onClick={() => setOpen(o => !o)}
          style={{
            background: 'transparent', border: 'none', color: C.muted,
            fontSize: 12, cursor: 'pointer', padding: '2px 6px',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {open ? t('close_cal') : t('open_cal')}
        </button>
      </div>

      {open && (
        <div style={{
          background: C.cardInner,
          borderRadius: 12,
          padding: '16px 12px',
          border: `1px solid ${C.sep}`,
        }}>
          {/* Header do calendário */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <button onClick={prevMonth} style={{ background: 'none', border: 'none', color: C.label, fontSize: 18, cursor: 'pointer', padding: '4px 8px' }}>‹</button>
            <span style={{ color: C.value, fontWeight: 700, fontSize: 14 }}>
              {MONTH_NAMES[viewMonth]} {viewYear}
            </span>
            <button onClick={nextMonth} style={{ background: 'none', border: 'none', color: C.label, fontSize: 18, cursor: 'pointer', padding: '4px 8px' }}>›</button>
          </div>

          {/* Dias da semana */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
            {DAY_NAMES.map(d => (
              <div key={d} style={{ textAlign: 'center', color: C.muted, fontSize: 10, fontWeight: 700, padding: '2px 0' }}>{d}</div>
            ))}
          </div>

          {/* Grid de dias */}
          {availDates === null ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: C.muted, fontSize: 13 }}>{t('checking_availability')}</div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3 }}>
                {/* Espaços vazios para alinhar o primeiro dia */}
                {Array.from({ length: firstDayOfMonth(viewYear, viewMonth) }).map((_, i) => (
                  <div key={`empty-${i}`} />
                ))}
                {Array.from({ length: daysInMonth(viewYear, viewMonth) }, (_, i) => i + 1).map(day => {
                  const key  = toKey(viewYear, viewMonth, day);
                  const date = new Date(viewYear, viewMonth, day);
                  const isPast = date < today;
                  const isAvail = !isPast && availDates[key] > 0;
                  const isSel  = selDate === key;
                  const isCurrent = savedDate && savedDate.startsWith(key);

                  return (
                    <button
                      key={day}
                      onClick={() => handleDayClick(key, isAvail)}
                      style={{
                        background: isSel ? C.accent : isCurrent ? '#1a3a5a' : isAvail ? '#1a3050' : 'transparent',
                        color: isSel ? C.pageBg : isPast ? C.muted : isAvail ? C.value : C.muted,
                        border: isCurrent ? `1px solid ${C.accent}` : '1px solid transparent',
                        borderRadius: 6,
                        padding: '6px 2px',
                        fontSize: 12,
                        fontWeight: isSel || isCurrent ? 700 : 400,
                        cursor: isAvail ? 'pointer' : 'default',
                        opacity: isPast ? 0.35 : 1,
                        textAlign: 'center',
                        WebkitTapHighlightColor: 'transparent',
                        textDecoration: !isAvail && !isPast && !isCurrent ? 'line-through' : 'none',
                      }}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>

              {/* Legenda */}
              <div style={{ display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: C.muted }}>
                  <span style={{ width: 10, height: 10, background: '#1a3050', borderRadius: 2, display: 'inline-block' }} /> {t('available')}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: C.muted }}>
                  <span style={{ width: 10, height: 10, background: 'transparent', borderRadius: 2, display: 'inline-block', textDecoration: 'line-through', fontSize: 10, color: C.muted }}>-</span> {t('sold_out')}
                </span>
                {savedDate && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: C.muted }}>
                    <span style={{ width: 10, height: 10, border: `1px solid ${C.accent}`, borderRadius: 2, display: 'inline-block' }} /> {t('current_date')}
                  </span>
                )}
              </div>

              {/* Nota */}
              <div style={{
                marginTop: 12,
                background: '#0f1c2e',
                borderRadius: 8,
                padding: '8px 12px',
                color: C.muted,
                fontSize: 12,
                lineHeight: 1.6,
                fontStyle: 'italic',
              }}>
                {t('calendar_note')}
              </div>

              {/* Botão salvar data */}
              {selDate && (
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    marginTop: 10,
                    width: '100%',
                    background: saving ? C.muted : C.accent,
                    color: C.pageBg,
                    border: 'none',
                    borderRadius: 10,
                    padding: '12px 0',
                    fontWeight: 700,
                    fontSize: 14,
                    cursor: saving ? 'default' : 'pointer',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  {saving ? t('saving') : `${t('confirm_date')} ${fmtDate(selDate)}`}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Componente de item de tour ───────────────────────────────────────────────

const TourItemCard = ({ b, idx, presaleId, accessToken, promo }) => {
  const t = window.__ftPresaleT || ((k) => k);
  const tour = b.travel?.[0] || {};
  // hasServiceId: true se o item tem um passeio/traslado associado
  const hasServiceId = !!(b.tour_id || b.translate_id || b.combo_id || b.travel_id || tour.id);
  const isTransfer   = b.travel_type === 'transfer' || b.type === 'transfer';

  const name        = tour.name || (isTransfer ? 'Transfer' : '—');
  const region = (tour.region_name || '').toLowerCase().trim();

  // Desconto automático por região e forma de pagamento
  const { pix: pixPct, card: cardPct } = regionPcts(region, isTransfer, promo);

  // Dados do site já vêm embutidos no response do backend (PreSaleService busca server-to-server)
  const sitePhoto    = b.site_foto     || null;
  const siteDesc     = b.site_descricao || null;
  const siteIncluded = b.site_incluso  || null;
  const siteMonths   = Array.isArray(b.site_meses) ? b.site_meses.map(m => String(m).trim().toLowerCase()) : [];

  const rawDesc     = tour.description || '';
  const sysDesc     = rawDesc && rawDesc !== 'false' && rawDesc.length > 3 ? rawDesc : '';
  // Sistema tem prioridade; fallback para dados do site
  const description = sysDesc || siteDesc || '';
  const included    = tour.whats_included  || siteIncluded || '';
  const notIncluded = tour.whats_not_included || '';

  const adults    = parseInt(b.adults_total) || 0;
  const kids      = parseInt(b.kids_total)   || 0;

  // Subtotal correto: soma dos totais de adultos + crianças
  const adultsAmt  = Number(b.adults_total_amount || 0);
  const kidsAmt    = Number(b.kids_total_amount   || 0);
  const subtotal   = adultsAmt + kidsAmt || Number(b.price || 0);
  const discPct    = Number(b.discount_total_sale || 0);
  const discVal    = discPct > 0
    ? subtotal * discPct / 100
    : Number(b.discount_total_amount || 0);
  const total      = subtotal - discVal;

  // Preço unitário por adulto
  const priceAdult = b.travel_type === 'tour'
    ? Number(tour.price || 0)
    : (adults > 0 ? subtotal / adults : 0);
  const priceKid = Number(tour.kids_price || 0);

  // Transfer type badge
  const transferLabel = b.transfer_type === 'in' ? 'Transfer IN' :
                        b.transfer_type === 'out' ? 'Transfer OUT' :
                        b.transfer_type ? `Transfer ${b.transfer_type}` : null;

  return (
    <Card style={{ padding: 0, overflow: 'hidden' }}>
      {/* Photo — sempre via API do site fulltour.com.br */}
      {hasServiceId && sitePhoto && (
        <div style={{ position: 'relative' }}>
          <img
            src={sitePhoto}
            alt={name}
            style={{
              width: '100%',
              height: 180,
              objectFit: 'cover',
              display: 'block',
            }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          {/* Gradient overlay on photo */}
          <div style={{
            position: 'absolute',
            bottom: 0, left: 0, right: 0,
            height: 80,
            background: `linear-gradient(to top, ${C.card}, transparent)`,
          }} />
          {transferLabel && (
            <div style={{ position: 'absolute', top: 12, right: 12 }}>
              <Badge bg="#4480A8">{transferLabel}</Badge>
            </div>
          )}
        </div>
      )}

      <div style={{ padding: 20 }}>
        {/* Item header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
          <div>
            <div style={{ color: C.accent, fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 4 }}>
              {t('item')} {idx + 1}
            </div>
            <div style={{ color: C.value, fontSize: 18, fontWeight: 700, lineHeight: 1.3 }}>
              {hasServiceId ? (((window.__ftPresaleLang || 'pt') !== 'pt' && b.site_titulo) ? b.site_titulo : name) : t('to_confirm_consultant')}
            </div>
            {region && (
              <div style={{ color: C.label, fontSize: 13, marginTop: 4 }}>
                📍 {region}
              </div>
            )}
            {!isTransfer && b.site_slug && (
              <a
                href={`https://fulltour.com.br/tour/${b.site_slug}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => { try { window.__ftTrackEvent?.('saiba_mais', name); } catch {} }}
                style={{
                  display: 'inline-block',
                  marginTop: 8,
                  color: C.accent,
                  fontSize: 13,
                  textDecoration: 'none',
                  fontWeight: 600,
                }}
              >
                {t('know_more')}
              </a>
            )}
          </div>
        </div>

        {!hasServiceId ? (
          <div style={{
            marginTop: 16,
            padding: '14px 16px',
            background: C.cardInner,
            borderRadius: 10,
            color: C.label,
            fontSize: 13,
            fontStyle: 'italic',
            lineHeight: 1.7,
            textAlign: 'center',
          }}>
            {t('service_details_pending')}
          </div>
        ) : (
          <>
            {/* Description — completa */}
            {description && (
              <div style={{ marginTop: 14 }}>
                <div style={{
                  padding: '12px 14px',
                  background: C.cardInner,
                  borderRadius: 10,
                  color: C.value,
                  fontSize: 13,
                  lineHeight: 1.7,
                }}>
                  {description}
                </div>
              </div>
            )}

            {/* Banner Day Use Farellones — tour ID 31 (sistema) ou 40 (site) */}
            {(/valle nevado.*farellones.*panoram/i.test(tour.name || '') || /farellones.*panoram/i.test(tour.name || '')) && (
              <div style={{ margin: '10px 0', padding: '14px 18px', backgroundColor: '#065f46', borderRadius: 10, textAlign: 'center' }}>
                <div style={{ color: '#6ee7b7', fontSize: 11, fontWeight: 700, marginBottom: 6, letterSpacing: 1 }}>{t('consultant_tip')}</div>
                <div style={{ color: '#ffffff', fontSize: 14, fontWeight: 700, lineHeight: 1.5 }}>
                  {t('dayuse_line1')}
                </div>
                <div style={{ color: '#a7f3d0', fontSize: 13, marginTop: 4 }}>
                  {t('dayuse_line2_pre')}<strong>{t('dayuse_line2_strong')}</strong>{t('dayuse_line2_post')}
                </div>
              </div>
            )}

            {/* Includes / Not includes */}
            {(included || notIncluded) && (
              <div style={{ display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
                {included && (
                  <div style={{
                    flex: 1,
                    minWidth: 120,
                    background: C.include,
                    borderRadius: 10,
                    padding: '10px 12px',
                  }}>
                    <div style={{ color: '#62DEC2', fontSize: 11, fontWeight: 700, marginBottom: 6 }}>{t('includes')}</div>
                    {included.split('\n').map((line, i) => line.trim() && (
                      <div key={i} style={{ color: '#c4f0e4', fontSize: 12, lineHeight: 1.6 }}>• {line.trim()}</div>
                    ))}
                  </div>
                )}
                {notIncluded && (
                  <div style={{
                    flex: 1,
                    minWidth: 120,
                    background: C.notInclude,
                    borderRadius: 10,
                    padding: '10px 12px',
                  }}>
                    <div style={{ color: '#fc8181', fontSize: 11, fontWeight: 700, marginBottom: 6 }}>{t('not_includes')}</div>
                    {notIncluded.split('\n').map((line, i) => line.trim() && (
                      <div key={i} style={{ color: '#fcc', fontSize: 12, lineHeight: 1.6 }}>• {line.trim()}</div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <Divider />

            {/* Date & passengers */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
              <div style={{ flex: 1, minWidth: 100, background: C.cardInner, borderRadius: 10, padding: '10px 14px' }}>
                <Label>{t('date')}</Label>
                {b.tour_date
                  ? <Value>{fmtDate(b.tour_date)}</Value>
                  : <span style={{ color: C.muted, fontSize: 13, fontStyle: 'italic' }}>{t('to_schedule')}</span>
                }
              </div>
              <div style={{ flex: 1, minWidth: 100, background: C.cardInner, borderRadius: 10, padding: '10px 14px' }}>
                <Label>{t('passengers')}</Label>
                <Value>{adults} {adults !== 1 ? t('adults') : t('adult')}{kids > 0 ? `, ${kids} ${kids !== 1 ? t('kids') : t('kid')}` : ''}</Value>
              </div>
            </div>

            {/* Date request / calendar */}
            {!isTransfer && presaleId && (
              <DateRequestSection
                tourId={b.travel_id}
                tourDate={b.tour_date}
                budgetId={b.id}
                presaleId={presaleId}
                accessToken={accessToken}
                tourName={b.travel?.[0]?.name || 'Passeio'}
              />
            )}

            {/* Pricing */}
            <div style={{ background: C.cardInner, borderRadius: 12, padding: '14px 16px', marginTop: 14 }}>
              <div style={{ color: C.accent, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 12 }}>
                {t('values')}
              </div>

              {b.modality === 'single' ? (
                /* Tour privado: preço por intervalo, sem breakdown por pessoa */
                <Row
                  label={<span>{adults + kids} {t('people')}</span>}
                  value={fmtMoney(subtotal)}
                />
              ) : (
                <>
                  {adults > 0 && (
                    <Row
                      label={<span>{adults} {adults !== 1 ? t('adults') : t('adult')}<span style={{ color: C.muted, fontSize: 12 }}> × {fmtMoney(priceAdult)}</span></span>}
                      value={fmtMoney(adultsAmt || subtotal)}
                    />
                  )}
                  {kids > 0 && (
                    <Row
                      label={<span>{kids} {kids !== 1 ? t('kids') : t('kid')}<span style={{ color: C.muted, fontSize: 12 }}> × {fmtMoney(priceKid)}</span></span>}
                      value={fmtMoney(kidsAmt)}
                    />
                  )}
                </>
              )}
              <Row label={t('subtotal')} value={fmtMoney(subtotal)} last={isTransfer} />

              {/* Desconto automático por forma de pagamento (só passeios) */}
              {!isTransfer && pixPct > 0 && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${C.sep}` }}>
                  <div style={{ color: C.label, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
                    {t('payment_discount')}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#68D391', fontSize: 13 }}>⚡ PIX <span style={{ fontSize: 11, opacity: 0.8 }}>({pixPct}{t('pix_disc_suffix')})</span></span>
                      <span style={{ color: '#68D391', fontWeight: 700, fontSize: 14 }}>
                        {fmtMoney(subtotal * (1 - pixPct / 100))}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: C.accent, fontSize: 13 }}>{t('card_label_short')} <span style={{ fontSize: 11, opacity: 0.8 }}>({cardPct}{t('pix_disc_suffix')})</span></span>
                      <span style={{ color: C.accent, fontWeight: 700, fontSize: 14 }}>
                        {fmtMoney(subtotal * (1 - cardPct / 100))}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Card>
  );
};

// ─── Comentários ─────────────────────────────────────────────────────────────

const CommentsSection = ({ saleId, token, comments, fetchComments, prefilledMessage, onPrefilledUsed, sectionRef }) => {
  const t = window.__ftPresaleT || ((k) => k);
  const [newMessage, setNewMessage]   = useState('');

  // Quando chegar mensagem pré-preenchida (ex: solicitação de data), preenche o campo e rola até aqui
  useEffect(() => {
    if (prefilledMessage) {
      setNewMessage(prefilledMessage);
      sectionRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      onPrefilledUsed?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefilledMessage]);
  const [authorName, setAuthorName]   = useState(() => {
    try { return localStorage.getItem('presale_author_name') || ''; } catch { return ''; }
  });
  const [askName, setAskName]         = useState(false);
  const [tempName, setTempName]       = useState('');
  const [sending, setSending]         = useState(false);
  const endRef = useRef(null);

  const prevCommentCount = useRef(0);
  useEffect(() => {
    if (comments.length > 0 && comments.length > prevCommentCount.current) {
      endRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    prevCommentCount.current = comments.length;
  }, [comments]);

  const send = () => {
    const name = authorName.trim();
    if (!name) { setAskName(true); return; }
    if (!newMessage.trim()) return;
    setSending(true);
    apiClient.httpInstance
      .post(`/customer/presales/${saleId}/comments?access_token=${token}`, {
        message: newMessage.trim(),
        author_name: name,
      })
      .then(() => { setNewMessage(''); fetchComments(); })
      .catch(() => alert(t('send_error')))
      .finally(() => setSending(false));
  };

  const confirmName = () => {
    const n = tempName.trim();
    if (!n) return;
    setAuthorName(n);
    try { localStorage.setItem('presale_author_name', n); } catch (e) {}
    setAskName(false);
    // will re-trigger send after state update
    setTimeout(send, 50);
  };

  return (
    <div ref={sectionRef}>
    <Card>
      <SectionTitle icon="💬">{t('chat_title')}</SectionTitle>

      {/* Thread */}
      <div style={{ maxHeight: 320, overflowY: 'auto', marginBottom: 14 }}>
        {comments.length === 0 && (
          <div style={{ color: C.muted, fontSize: 13, textAlign: 'center', padding: '20px 0', fontStyle: 'italic' }}>
            {t('no_messages')}
          </div>
        )}
        {comments.map((c) => {
          const isClient = c.author_type === 'customer';
          return (
            <div key={c.id} style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: isClient ? 'flex-end' : 'flex-start',
              marginBottom: 12,
            }}>
              <div style={{
                background: isClient ? C.bubbleClient : C.bubbleAgent,
                borderRadius: isClient ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                padding: '10px 14px',
                maxWidth: '80%',
              }}>
                <div style={{ color: isClient ? C.accent : '#68D391', fontSize: 11, fontWeight: 700, marginBottom: 4 }}>
                  {c.author_name}
                </div>
                <div style={{ color: C.value, fontSize: 14, lineHeight: 1.5 }}>{c.message}</div>
                <div style={{ color: C.muted, fontSize: 10, marginTop: 6, textAlign: 'right' }}>
                  {fmtDateTime(c.created_at)}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {/* Ask name modal */}
      {askName && (
        <div style={{ marginBottom: 12 }}>
          <p style={{ color: C.label, fontSize: 13, marginBottom: 8 }}>{t('whats_your_name')}</p>
          <input
            type="text"
            value={tempName}
            onChange={(e) => setTempName(e.target.value)}
            placeholder={t('your_name')}
            style={{
              width: '100%', boxSizing: 'border-box',
              background: C.cardInner, border: `1px solid ${C.sep}`,
              borderRadius: 8, color: C.value, fontSize: 16,
              padding: '10px 12px', marginBottom: 8,
            }}
            onKeyDown={(e) => e.key === 'Enter' && confirmName()}
          />
          <button
            onClick={confirmName}
            style={{
              background: C.accent, color: C.pageBg, border: 'none',
              borderRadius: 8, padding: '10px 24px', fontWeight: 700,
              fontSize: 14, cursor: 'pointer', width: '100%',
            }}
          >
            {t('confirm_and_send')}
          </button>
        </div>
      )}

      {/* Input */}
      {!askName && (
        <div style={{ display: 'flex', gap: 8 }}>
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={t('write_message')}
            rows={2}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            style={{
              flex: 1,
              background: C.cardInner,
              border: `1px solid ${C.sep}`,
              borderRadius: 10,
              color: C.value,
              fontSize: 15,
              padding: '10px 12px',
              resize: 'none',
              outline: 'none',
              boxSizing: 'border-box',
              width: '100%',
            }}
          />
          <button
            onClick={send}
            disabled={sending || !newMessage.trim()}
            style={{
              background: sending || !newMessage.trim() ? C.muted : C.accent,
              color: C.pageBg,
              border: 'none',
              borderRadius: 10,
              padding: '0 18px',
              fontWeight: 700,
              fontSize: 18,
              cursor: sending || !newMessage.trim() ? 'not-allowed' : 'pointer',
              flexShrink: 0,
            }}
          >
            {sending ? '...' : '➤'}
          </button>
        </div>
      )}
    </Card>
    </div>
  );
};

// ─── Componente principal ────────────────────────────────────────────────────

// ─── Toast elegante ──────────────────────────────────────────────────────────
const Toast = ({ toast }) => {
  if (!toast) return null;
  const isSuccess = toast.type === 'success';
  const isWarning = toast.type === 'warning';
  const icon     = isSuccess ? '✅' : isWarning ? '⚠️' : '❌';
  const color    = isSuccess ? '#68D391' : isWarning ? '#F6AD55' : '#fc8181';
  return (
    <div style={{
      position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)',
      zIndex: 9999, minWidth: 280, maxWidth: 360,
      background: isSuccess ? '#1a3d2a' : '#3d1a1a',
      border: `1px solid ${isSuccess ? '#68D391' : '#e05252'}`,
      borderRadius: 14, padding: '14px 20px',
      display: 'flex', alignItems: 'center', gap: 12,
      boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
      animation: 'fadeInUp 0.25s ease',
    }}>
      <span style={{ fontSize: 20 }}>{icon}</span>
      <span style={{ color, fontSize: 14, fontWeight: 600 }}>
        {toast.msg}
      </span>
    </div>
  );
};

const PresalePage = () => {
  const { sale_id, token } = useParams();
  // ── Idioma (Presale-1, 2026-06-05) ──
  const [plang, setPlang] = useState(() => {
    try { return localStorage.getItem('presale_lang') || 'pt'; } catch { return 'pt'; }
  });
  const t = makeT(plang);
  const changeLang = (l) => { setPlang(l); try { localStorage.setItem('presale_lang', l); } catch {} };
  const [voucher, setVoucher]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [comments, setComments] = useState([]);
  const [actionLoading, setActionLoading]     = useState(false);
  const [showRefuse, setShowRefuse]           = useState(false);
  const [refuseReason, setRefuseReason]       = useState('');
  const [toast, setToast]                     = useState(null);
  const [chatPreFill, setChatPreFill]         = useState(null);
  const chatRef = useRef(null);

  // ── Pagamento ────────────────────────────────────────────────────────────
  const [showPayModal, setShowPayModal]       = useState(false);
  const [paySettings, setPaySettings]         = useState(null);   // {installments, item_discounts, totals}
  const [paySettingsLoading, setPaySettingsLoading] = useState(false);
  const pollRef                               = useRef(null);

  // Seleção inline na coluna direita (igual FTApp)
  const [rightMethod, setRightMethod]         = useState('pix');
  const [rightPixSplit, setRightPixSplit]     = useState(30);
  const [rightInstallments, setRightInstallments] = useState(1);

  // ── Pagamento internacional (EN/ES) — estado elevado para mostrar no card ──
  const [intlCurrency, setIntlCurrency]       = useState(null);
  const [intlRate, setIntlRate]               = useState(null);
  const [intlAmount, setIntlAmount]           = useState(null);
  const [intlRateLoading, setIntlRateLoading] = useState(false);
  const [intlRateError, setIntlRateError]     = useState(false);
  const [intlRetryKey, setIntlRetryKey]       = useState(0);

  // Reseta o .App que auth/app.css trava com position:fixed + overflow:hidden
  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    const appEl = document.querySelector('.App');
    if (appEl) {
      appEl.style.overflow = 'visible';
      appEl.style.position = 'relative';
      appEl.style.height   = 'auto';
    }
    document.body.style.height              = 'auto';
    document.body.style.overflow            = '';
    document.documentElement.style.height  = 'auto';
    document.documentElement.style.overflow = '';
    window.scrollTo(0, 0);
  }, []);

  const showToast = (msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Bloqueia tradução automática do navegador — corrompe o DOM do React (2026-06-04).
  // A tradução oficial da presale virá com o seletor de bandeiras (Presale-1).
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'google';
    meta.content = 'notranslate';
    document.head.appendChild(meta);
    document.documentElement.setAttribute('translate', 'no');
    document.documentElement.classList.add('notranslate');
    return () => {
      document.head.removeChild(meta);
      document.documentElement.removeAttribute('translate');
      document.documentElement.classList.remove('notranslate');
    };
  }, []);

  // ── Tracking interno (2026-06-04): movimentos do cliente viram comentário
  // interno no chat do consultor (+ sininho nos eventos quentes).
  // Usuário logado do sistema abrindo o link NÃO dispara nada.
  const isInternalUser = (() => {
    try { return !!JSON.parse(localStorage.getItem('user'))?.token; } catch { return false; }
  })();
  const trackEvent = (event, label = '') => {
    if (isInternalUser) return;
    try {
      apiClient.httpInstance
        .post(`/customer/presales/${sale_id}/track-event?access_token=${token}`, { event, label })
        .catch(() => {});
    } catch { /* tracking nunca quebra a página */ }
  };
  useEffect(() => { window.__ftTrackEvent = trackEvent; return () => { delete window.__ftTrackEvent; }; });
  // Disponibiliza o tradutor para os componentes de módulo (DateRequestSection,
  // TourItemCard, CommentsSection, Toast). Atribuição síncrona durante o render
  // para que os filhos já recebam o idioma certo no primeiro render.
  window.__ftPresaleT = t;
  window.__ftPresaleLang = plang;
  useEffect(() => { window.__ftPresaleT = t; return () => { delete window.__ftPresaleT; }; });
  useEffect(() => {
    if (isInternalUser) return;
    try {
      apiClient.httpInstance
        .post(`/customer/presales/${sale_id}/track-view?access_token=${token}`)
        .catch(() => {});
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // EN → auto-seleciona USD ao carregar; ES → aguarda clique; PT → sem conversão
  useEffect(() => {
    if (plang === 'en') {
      setIntlCurrency(prev => prev === 'USD' ? prev : 'USD');
    } else if (plang !== 'es') {
      setIntlCurrency(null);
      setIntlRate(null);
      setIntlAmount(null);
    } else {
      // ES: mantém moeda selecionada mas reseta cotação (pode ter mudado)
      setIntlRate(null);
      setIntlAmount(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plang]);

  // Busca cotação sempre que a moeda internacional muda
  useEffect(() => {
    if (!intlCurrency || !sale_id || !token) {
      setIntlAmount(null);
      setIntlRate(null);
      return;
    }
    setIntlAmount(null);
    setIntlRate(null);
    setIntlRateLoading(true);
    setIntlRateError(false);
    apiClient.httpInstance
      .get(`/customer/presales/${sale_id}/pay-link/rate?currency=${intlCurrency}&access_token=${token}`)
      .then((r) => {
        setIntlAmount(r.data?.amount_foreign ?? null);
        setIntlRate(r.data?.rate ?? null);
      })
      .catch(() => setIntlRateError(true))
      .finally(() => setIntlRateLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intlCurrency, intlRetryKey]);

  const fetchVoucher = () => {
    setLoading(true);
    apiClient.httpInstance
      .get(`/customer/get_presale_voucher/${sale_id}?access_token=${token}&lang=${plang}`)
      .then((r) => {
        if (r.status === 200) {
          setVoucher(r.data);
          window.scrollTo({ top: 0, behavior: 'instant' });
          // GA4: view_cart — orçamento carregado pelo cliente
          try {
            const _buds = r.data?.budgets || [];
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ ecommerce: null });
            window.dataLayer.push({
              event: 'view_cart',
              ecommerce: {
                currency: 'BRL',
                value:    _buds.reduce((s, b) => s + Number(b.adults_total_amount || 0) + Number(b.kids_total_amount || 0) || Number(b.price || 0), 0),
                items:    _buds.map((b, i) => ({
                  item_id:       String(b.travel_id || b.tour_id || b.id || i),
                  item_name:     b.travel?.[0]?.name || (b.travel_type === 'transfer' ? 'Transfer' : 'Serviço'),
                  item_category: b.travel?.[0]?.region_name || '',
                  price:         Math.round((Number(b.adults_total_amount || 0) + Number(b.kids_total_amount || 0) || Number(b.price || 0)) * 100) / 100,
                  quantity:      (parseInt(b.adults_total) || 0) + (parseInt(b.kids_total) || 0) || 1,
                })),
              },
            });
          } catch (_) {}
        }
      })
      .catch(() => setError('load_error'))
      .finally(() => setLoading(false));
  };

  const fetchComments = () => {
    apiClient.httpInstance
      .get(`/customer/presales/${sale_id}/comments?access_token=${token}`)
      .then((r) => r.status === 200 && setComments(r.data || []))
      .catch(() => {});
  };

  // Carrega paySettings sempre que o voucher chega — useEffect separado para garantir execução
  // Fix 2026-10-13 (BT6ACB9F8B57AAB): antes só buscava UMA vez (`|| paySettings`). Se o cliente
  // deixava a página aberta e o desconto gerencial era aprovado depois, o modal do PIX usava os
  // totais velhos (cobrou R$ 495,54 em vez de R$ 458,32 e mostrou outro valor no resumo).
  // Agora busca de novo sempre que o desconto aprovado ou o conteúdo do orçamento mudar.
  const paySettingsKeyRef = useRef(null);
  useEffect(() => {
    if (!voucher) return;
    const settingsKey = [
      voucher?.approval_status, voucher?.approval_type, voucher?.approval_manager_value, voucher?.total,
      (voucher?.budgets || []).map((b) => `${b.id}:${b.total_amount ?? b.price ?? ''}`).join(','),
    ].join('|');
    if (paySettings && paySettingsKeyRef.current === settingsKey) return;
    const status = voucher?.customer_status;
    const exp    = voucher?.expires_at;
    const active = !status
                   && !(exp && new Date(exp) < new Date())
                   && voucher?.status !== 'canceled'
                   && voucher?.status !== 'sold';
    if (!active) return;
    paySettingsKeyRef.current = settingsKey;
    apiClient.httpInstance
      .get(`/customer/presales/${sale_id}/payment-settings?access_token=${token}`)
      .then((ps) => setPaySettings(ps.data))
      .catch((e) => {
        paySettingsKeyRef.current = null; // tenta de novo no próximo ciclo do voucher
        console.error('[presale] paySettings error:', e?.response?.status, e?.response?.data);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voucher]);

  useEffect(() => {
    fetchVoucher();
    fetchComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sale_id, token, plang]);

  // Polling silencioso — atualiza dados quando consultor faz mudanças no admin
  useEffect(() => {
    const interval = setInterval(() => {
      // Só atualiza se a página estiver visível e não tiver modal de pagamento aberto
      // (setVoucher re-renderiza o pai e o PaymentModal inline é recriado — perderia o que o cliente digitou)
      if (document.hidden || showPayModal) return;
      apiClient.httpInstance
        .get(`/customer/get_presale_voucher/${sale_id}?access_token=${token}&lang=${plang}`)
        .then((r) => {
          if (r.status === 200) {
            setVoucher(r.data);
          }
        })
        .catch(() => {});
      fetchComments();
    }, 30000); // a cada 30 segundos
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sale_id, token, showPayModal]);

  const handleApprove = () => {
    setActionLoading(true);
    apiClient.httpInstance
      .post(`/customer/presales/${sale_id}/approve?access_token=${token}`)
      .then(() => { window.location.reload(); })
      .catch((e) => showToast(e?.response?.data?.message || t('approve_error')))
      .finally(() => setActionLoading(false));
  };

  const handleRefuse = () => {
    setActionLoading(true);
    apiClient.httpInstance
      .post(`/customer/presales/${sale_id}/refuse?access_token=${token}`, { reason: refuseReason })
      .then(() => { window.location.reload(); })
      .catch((e) => showToast(e?.response?.data?.message || t('refuse_error')))
      .finally(() => setActionLoading(false));
  };

  const handleOpenPayModal = () => {
    setShowPayModal(true);
    trackEvent('checkout_open');
    // GA4: begin_checkout — dispara ao clicar "Aprovar e pagar"
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'begin_checkout',
        ecommerce: {
          currency: 'BRL',
          value: Math.round(totalPixCalc * 100) / 100,
          items: budgets.map((b, i) => ({
            item_id:       String(b.travel_id || b.tour_id || b.id || i),
            item_name:     b.travel?.[0]?.name || (b.travel_type === 'transfer' ? 'Transfer' : 'Serviço'),
            item_category: b.travel?.[0]?.region_name || '',
            price:         Math.round((Number(b.adults_total_amount || 0) + Number(b.kids_total_amount || 0) || Number(b.price || 0)) * 100) / 100,
            quantity:      (parseInt(b.adults_total) || 0) + (parseInt(b.kids_total) || 0) || 1,
          })),
        },
      });
    } catch (_) {}
    // Sempre busca os totais de novo ao abrir o pagamento (fix 2026-10-13, BT6ACB9F8B57AAB):
    // reaproveitar os totais carregados horas antes cobrava sem o desconto gerencial aprovado
    // depois. Só mostra o "carregando" se ainda não havia totais.
    if (!paySettings) setPaySettingsLoading(true);
    apiClient.httpInstance
      .get(`/customer/presales/${sale_id}/payment-settings?access_token=${token}`)
      .then((r) => setPaySettings(r.data))
      .catch(() => { if (!paySettings) showToast(t('payment_settings_error')); })
      .finally(() => setPaySettingsLoading(false));
  };

  // ── Dados derivados ──────────────────────────────────────────────────────

  const logo          = voucher?.company?.logo ? imgUrl(voucher.company.logo) : null;
  const expired       = voucher ? isExpired(voucher.expires_at) : false;
  const customerStatus = voucher?.customer_status;
  const canAct        = !expired && customerStatus == null
                        && voucher?.status !== 'canceled'
                        && voucher?.status !== 'sold';

  const budgets = voucher?.budgets || [];

  // ── Black Friday nov/2026 ────────────────────────────────────────────────────
  // O backend manda `promo` só quando o orçamento foi criado em nov/2026 e ainda está dentro do
  // prazo (30/11). paySettings (mais recente) tem prioridade; antes dele carregar usa voucher.bf_promo.
  // Só vale enquanto o orçamento ainda pode ser pago (canAct) — o tema some em orçamento pago/recusado.
  const bfPromo = (paySettings ? paySettings.promo : voucher?.bf_promo) || null;
  const bfOn    = !!(bfPromo && bfPromo.active && canAct && voucher?.payment_status !== 'paid' && voucher?.payment_status !== 'partial');

  // Tours PIX-exclusivo: Monteluz (#139) e Alyan Sunset (#58) — somente quando data no mês vigente
  const PIX_ONLY_TOUR_IDS = [139, 58];
  const _currentMonth = new Date().toISOString().slice(0, 7);
  // eslint-disable-next-line eqeqeq
  // Trava manual de cartão (consultor/gestão, 2026-10-21): enquanto não liberada pela gestão, só Pix
  // O cartão continua visível: ao escolhê-lo aparece o aviso de autorização gerencial (ou Pix), como no site.
  const cardLocked = !!(paySettings?.card_hold?.active && !paySettings.card_hold.allowed);
  const hasMonteluzCurrentMonth = budgets.some(b =>
    PIX_ONLY_TOUR_IDS.some(id => b.tour_id == id) &&
    b.tour_date && b.tour_date.slice(0, 7) === _currentMonth
  );

  // Força PIX 100% quando Monteluz está no orçamento
  useEffect(() => {
    if (hasMonteluzCurrentMonth) {
      setRightMethod('pix');
      setRightPixSplit(100);
    }
  }, [hasMonteluzCurrentMonth]);

  // Totais usando os mesmos campos dos cards individuais
  const HIGH_REGIONS_SUMMARY = ['santiago', 'atacama'];
  const totalBruto = budgets.reduce((s, b) => {
    const a = Number(b.adults_total_amount || 0);
    const k = Number(b.kids_total_amount   || 0);
    return s + (a + k || Number(b.price || 0));
  }, 0);
  const totalDesconto = budgets.reduce((s, b) => {
    const a   = Number(b.adults_total_amount || 0);
    const k   = Number(b.kids_total_amount   || 0);
    const sub = a + k || Number(b.price || 0);
    const pct = Number(b.discount_total_sale || 0);
    return s + (pct > 0 ? sub * pct / 100 : Number(b.discount_total_amount || 0));
  }, 0);
  const totalLiquido = totalBruto - totalDesconto;

  // Totais com desconto automático por pagamento (sem desconto gerencial — aplicado depois)
  const { totalPix: totalPixBase, totalCard: totalCardBase, toursPix: toursPixBase, toursCard: toursCardBase } = budgets.reduce((acc, b) => {
    const tour       = b.travel?.[0] || {};
    const isTransfer = b.travel_type === 'transfer' || b.type === 'transfer';
    const region     = (tour.region_name || '').toLowerCase().trim();
    const a   = Number(b.adults_total_amount || 0);
    const k   = Number(b.kids_total_amount   || 0);
    const sub = a + k || Number(b.price || 0);
    const { pix: pixPctB, card: cardPctB } = regionPcts(region, isTransfer, bfOn ? bfPromo : null);
    acc.totalPix  += sub * (1 - pixPctB  / 100);
    acc.totalCard += sub * (1 - cardPctB / 100);
    if (!isTransfer) {
      acc.toursPix  += sub * (1 - pixPctB  / 100);
      acc.toursCard += sub * (1 - cardPctB / 100);
    }
    return acc;
  }, { totalPix: 0, totalCard: 0, toursPix: 0, toursCard: 0 });

  // Selo de % (2026-06-04): exibe a % NOMINAL quando todos os itens elegíveis têm a
  // mesma taxa (ex.: 12% PIX / 10% cartão em Santiago). A % efetiva sobre o total
  // fica menor quando há transfers (0%) na cesta e gerava questionamento de cliente.
  // Cesta mista de regiões → cai para a % efetiva calculada.
  const _eligibleHigh = budgets
    .filter(b => !(b.travel_type === 'transfer' || b.type === 'transfer'))
    .map(b => HIGH_REGIONS_SUMMARY.includes(((b.travel?.[0] || {}).region_name || '').toLowerCase().trim()));
  const _uniformPct = _eligibleHigh.length > 0 && _eligibleHigh.every(h => h === _eligibleHigh[0]);
  const _bfPcts      = regionPcts(_eligibleHigh[0] ? 'santiago' : 'outra', false, bfOn ? bfPromo : null);
  const pixBadgePct  = _uniformPct ? _bfPcts.pix  : null;
  const cardBadgePct = _uniformPct ? _bfPcts.card : null;
  const hasTransferInBasket = budgets.some(b => b.travel_type === 'transfer' || b.type === 'transfer');
  const transferNote = hasTransferInBasket && _uniformPct ? t('transfer_note') : '';

  // Desconto gerencial aprovado — aplica sobre o bruto, proporcional ao total PIX/Cartão
  // Base para desconto gerencial % — exclui transfers (transfers não têm desconto)
  const totalBrutoTours = budgets.reduce((s, b) => {
    if (b.travel_type === 'transfer' || b.type === 'transfer') return s;
    const a = Number(b.adults_total_amount || 0);
    const k = Number(b.kids_total_amount   || 0);
    return s + (a + k || Number(b.price || 0));
  }, 0);

  const managerDiscount = (() => {
    if (voucher?.approval_status !== 'approved') return 0;
    if (!['discount_pct', 'discount_value'].includes(voucher?.approval_type)) return 0;
    const val = parseFloat(voucher?.approval_manager_value || 0);
    if (!val) return 0;
    if (voucher.approval_type === 'discount_pct') return Math.round(totalBrutoTours * val / 100 * 100) / 100;
    return Math.round(val * 100) / 100;
  })();
  const managerDiscountPct = totalBruto > 0 ? Math.round(managerDiscount / totalBruto * 10000) / 100 : 0;
  // Regra única (igual ao backend applyManagerDiscount e à conversão em venda): o desconto
  // gerencial incide SÓ nos passeios e vem ANTES do PIX/Cartão (que também só incide nos
  // passeios). Antes se multiplicava por uma taxa misturada passeios+transfers (BT6ACB9F8B57AAB:
  // card mostrava 1.523,90 enquanto a conta certa era 1.527,72).
  const totalPixCalc  = Math.round((totalPixBase  - managerDiscount * (totalBrutoTours > 0 ? toursPixBase  / totalBrutoTours : 0)) * 100) / 100;
  const totalCardCalc = Math.round((totalCardBase - managerDiscount * (totalBrutoTours > 0 ? toursCardBase / totalBrutoTours : 0)) * 100) / 100;

  // Nome do cliente — testa cada candidato e usa o primeiro que não pareça telefone
  const isPhonelike = (str) => !str || /^[+\d\s()./-]+$/.test(str.trim());
  const pickName = (...candidates) => {
    for (const c of candidates) {
      if (c && !isPhonelike(c)) return c;
    }
    return null;
  };
  const clientName =
    pickName(
      voucher?.customer?.name,        // cliente vinculado (Customer model)
      voucher?.name,                   // nome editável do presale (campo principal)
      voucher?.customerData?.name,     // fallback legado
    ) ||
    voucher?.customerData?.email ||
    voucher?.email ||
    'Cliente';
  const firstName = clientName.split(/[\s@]/)[0];

  // ── PaymentModal ──────────────────────────────────────────────────────────

  const PaymentModal = () => {
    // Fix 2026-06-06: documento inválido (ex: consultor inventou "123456") não conta
    // como CPF — força a etapa de CPF para o cliente digitar 11 dígitos válidos e
    // evita 400 do Asaas. CPF = 11 dígitos; CNPJ = 14 (empresa).
    const docDigits = (voucher?.customerData?.document || '').replace(/\D/g,'');
    const hasCpf = docDigits.length === 11 || docDigits.length === 14;
    const [step, setStep]               = React.useState(hasCpf ? 'dates' : 'cpf');
    const [method, setMethod]           = React.useState(rightMethod);
    const [availability, setAvailability] = React.useState(null);
    const [availLoading, setAvailLoading] = React.useState(false);
    const [installments, setInstallments] = React.useState(rightInstallments);
    const [pixData, setPixData]         = React.useState(null);
    const [payLoading, setPayLoading]   = React.useState(false);
    const [pixSplit, setPixSplit]       = React.useState(rightPixSplit);
    const [cardRefusalMsg, setCardRefusalMsg] = React.useState(null);
    // Trava manual de cartão (2026-10-21): texto em 3 idiomas e ações da tela de autorização
    const [holdLoading, setHoldLoading] = React.useState(false);
    const HL = (pt, en, es) => (plang === 'en' ? en : plang === 'es' ? es : pt);
    const holdState = paySettings?.card_hold?.state || 'needs_release';
    const requestCardRelease = () => {
      setHoldLoading(true);
      apiClient.httpInstance
        .post(`/customer/presales/${sale_id}/card-release-request?access_token=${token}`)
        .then((r) => setPaySettings((p) => ({ ...(p || {}), card_hold: r.data?.card_hold || p?.card_hold })))
        .catch(() => showToast(t('payment_error')))
        .finally(() => setHoldLoading(false));
    };
    const refreshCardHold = () => {
      setHoldLoading(true);
      apiClient.httpInstance
        .get(`/customer/presales/${sale_id}/payment-settings?access_token=${token}`)
        .then((r) => setPaySettings(r.data))
        .catch(() => {})
        .finally(() => setHoldLoading(false));
    };
    // Pagamento internacional usa intlCurrency/intlRate/intlAmount do estado pai (closure)
    const [payLinkLoading, setPayLinkLoading]     = React.useState(false);
    const [payLinkUrl, setPayLinkUrl]             = React.useState(null);
    // CPF step
    const [cpfValue, setCpfValue]       = React.useState('');
    const [cpfLoading, setCpfLoading]   = React.useState(false);

    const formatCpf = (v) => {
      const d = v.replace(/\D/g,'').slice(0,11);
      if (d.length <= 3) return d;
      if (d.length <= 6) return `${d.slice(0,3)}.${d.slice(3)}`;
      if (d.length <= 9) return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6)}`;
      return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9)}`;
    };

    // Valida o dígito verificador do CPF (não só o tamanho) — evita CPF inválido chegar no
    // Asaas e travar a venda com erro genérico. Rejeita sequências repetidas e dígitos errados.
    const cpfIsValid = (raw) => {
      const c = String(raw || '').replace(/\D/g, '');
      if (c.length !== 11 || /^(\d)\1{10}$/.test(c)) return false;
      for (let tt = 9; tt < 11; tt++) {
        let s = 0;
        for (let i = 0; i < tt; i++) s += parseInt(c[i], 10) * ((tt + 1) - i);
        let dig = (s * 10) % 11;
        if (dig === 10) dig = 0;
        if (dig !== parseInt(c[tt], 10)) return false;
      }
      return true;
    };
    const cpfDigits    = cpfValue.replace(/\D/g, '');
    const cpfShowError = cpfDigits.length === 11 && !cpfIsValid(cpfDigits);
    // CPF/Asaas é só do fluxo PT (internacional paga via Nautt/Aloha, sem CPF) — msg só em PT.
    const cpfMsg = 'CPF inválido — confira os números digitados.';

    const handleSaveCpf = () => {
      const digits = cpfValue.replace(/\D/g,'');
      if (!cpfIsValid(digits)) { showToast(cpfMsg); return; }
      setCpfLoading(true);
      apiClient.httpInstance
        .put(`/customer/presales/${sale_id}/update-document?access_token=${token}`, { document: digits })
        .then(() => {
          // Atualiza o voucher local para não pedir de novo
          if (voucher?.customerData) voucher.customerData.document = digits;
          setStep('dates');
          // Checa datas
          setAvailLoading(true);
          apiClient.httpInstance
            .get(`/customer/presales/${sale_id}/check-availability?access_token=${token}`)
            .then((r) => { setAvailability(r.data); })
            .catch(() => showToast(t('availability_error')))
            .finally(() => setAvailLoading(false));
        })
        .catch((e) => showToast(e?.response?.data?.message || t('cpf_save_error')))
        .finally(() => setCpfLoading(false));
    };

    // Antifraude: começa a gerar o token do Turnstile assim que o cliente abre o pagamento (fica pronto no clique)
    React.useEffect(() => {
      if (showPayModal) prefetchTurnstile();
    }, [showPayModal]);

    // Sincroniza seleção da coluna direita ao abrir e auto-checa datas
    React.useEffect(() => {
      if (!showPayModal) {
        setStep(hasCpf ? 'dates' : 'cpf');
        setAvailability(null);
        setPixData(null);
        return;
      }
      setMethod(rightMethod);
      setPixSplit(rightPixSplit);
      setInstallments(rightInstallments);
      if (hasCpf) {
        // Auto-checa datas ao abrir
        setAvailLoading(true);
        apiClient.httpInstance
          .get(`/customer/presales/${sale_id}/check-availability?access_token=${token}`)
          .then((r) => { setAvailability(r.data); setStep('dates'); })
          .catch(() => showToast(t('availability_error')))
          .finally(() => setAvailLoading(false));
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showPayModal]);
    const [cardForm, setCardForm]       = React.useState({
      holderName: '', number: '', expiryMonth: '', expiryYear: '', ccv: '',
      postalCode: '', addressNumber: '', address: '', province: '',
    });
    // CEP com 8 dígitos → ViaCEP preenche rua/bairro (editáveis). CEP não encontrado ou titular de fora: o cliente digita à mão.
    // cepStatus: '' (nada) | 'loading' | 'found' (achou, campos preenchidos) | 'manual' (não achou / código de fora: cliente preenche)
    const [cepStatus, setCepStatus] = React.useState('');
    const cepLookupRef = React.useRef('');
    const onPostalChange = (raw) => {
      const v = raw.replace(/[^0-9A-Za-z]/g, '').slice(0, 10);
      setCardForm(f => ({ ...f, postalCode: v }));
      if (/^\d{8}$/.test(v)) {
        if (cepLookupRef.current === v) return;
        cepLookupRef.current = v;
        setCepStatus('loading');
        fetch(`https://viacep.com.br/ws/${v}/json/`)
          .then((r) => r.json())
          .then((j) => {
            if (cepLookupRef.current !== v) return;
            if (!j || j.erro) { setCepStatus('manual'); return; }
            setCardForm(f => ({ ...f, address: j.logradouro || f.address, province: j.bairro || j.localidade || f.province }));
            setCepStatus('found');
          })
          .catch(() => { if (cepLookupRef.current === v) setCepStatus('manual'); });
      } else {
        cepLookupRef.current = '';
        setCepStatus(v.length >= 4 ? 'manual' : '');
      }
    };
    const pollRefLocal = React.useRef(null);

    // ── Pagamento externo (Nautt USD / Aloha LatAm) ───────────────────────────
    const createExtPayLink = () => {
      if (pollRefLocal.current) clearInterval(pollRefLocal.current);
      setPayLinkLoading(true);
      setPayLinkUrl(null);
      setStep('ext_pay');
      apiClient.httpInstance
        .post(`/customer/presales/${sale_id}/pay-link?access_token=${token}`, {
          currency: intlCurrency,
        })
        .then((r) => {
          const url = r.data?.payment_url;
          setPayLinkUrl(url || null);
          setPayLinkLoading(false);
          if (url) window.open(url, '_blank', 'noopener,noreferrer');
          // Polling: verifica confirmação a cada 5s
          pollRefLocal.current = setInterval(() => {
            apiClient.httpInstance
              .get(`/customer/presales/${sale_id}/pay-link/status?access_token=${token}`)
              .then((sr) => {
                if (sr.data?.paid) {
                  clearInterval(pollRefLocal.current);
                  setStep('success');
                }
              })
              .catch(() => {});
          }, 5000);
        })
        .catch((e) => {
          setPayLinkLoading(false);
          showToast(e?.response?.data?.message || t('ext_pay_error'));
          setStep('method');
        });
    };

    const cfg     = paySettings?.installments || {};
    const maxInst = cfg.max_installments || 12;
    const maxFree = cfg.max_no_interest_installments ?? 1;  // sem fallback fixo — usa exatamente o configurado
    const rate    = cfg.interest_rate || 1;
    const minVal  = cfg.min_installment_value || 5;
    const items   = paySettings?.item_discounts || [];
    const totals  = paySettings?.totals || {};

    const totalPix      = totals.pix  || 0;
    const totalCard     = totals.card || 0;
    const chargeAmount  = Math.round(totalPix * pixSplit / 100 * 100) / 100;
    const restAmount    = Math.round((totalPix - chargeAmount) * 100) / 100;

    const calcInstallmentValue = (total, n) => {
      if (n <= maxFree) return total / n;
      return total * Math.pow(1 + rate / 100, n) / n;
    };

    const buildInstallments = (total) => {
      const opts = [];
      for (let n = 1; n <= maxInst; n++) {
        const val = calcInstallmentValue(total, n);
        if (val < minVal) break;
        opts.push({ n, val: round2(val), hasInterest: n > maxFree });
      }
      return opts;
    };

    const round2 = (v) => Math.round(v * 100) / 100;

    const fmtR = (v) => `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const checkDates = () => {
      setAvailLoading(true);
      apiClient.httpInstance
        .get(`/customer/presales/${sale_id}/check-availability?access_token=${token}`)
        .then((r) => {
          setAvailability(r.data);
          setStep('dates');
        })
        .catch(() => showToast(t('availability_error')))
        .finally(() => setAvailLoading(false));
    };

    const handleConfirmMethod = () => {
      if (!method) return;
      checkDates();
    };

    const handleProceedFromDates = () => {
      if (!availability?.all_ok) return;
      // EN/ES — cotação já está no card; vai direto para criar o payment link
      if (plang !== 'pt') {
        createExtPayLink();
        return;
      }
      // PT — fluxo normal PIX/cartão
      setStep(method === 'pix' ? 'pix' : 'card');
      if (method === 'pix') createPixCharge();
    };

    const createPixCharge = () => {
      setPayLoading(true);
      apiClient.httpInstance
        .post(`/customer/presales/${sale_id}/pay?access_token=${token}`, {
          payment_method: 'pix',
          total: chargeAmount,
          installments: 1,
          ...(pixSplit < 100 ? { partial_pct: pixSplit } : {}),
        })
        .then((r) => {
          if (r.data?.already_paid) { setStep('success'); return; }
          setPixData(r.data?.pix || null);
          setStep('pix');
          // Polling
          pollRefLocal.current = setInterval(() => {
            apiClient.httpInstance
              .get(`/customer/presales/${sale_id}/pay/status?access_token=${token}`)
              .then((sr) => {
                if (sr.data?.paid) {
                  clearInterval(pollRefLocal.current);
                  setStep('success');
                  // GA4: purchase — PIX confirmado
                  try {
                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({ ecommerce: null });
                    window.dataLayer.push({
                      event: 'purchase',
                      ecommerce: {
                        transaction_id: String(voucher?.references || voucher?.id || sale_id),
                        value:          Math.round(totalPix * 100) / 100,
                        currency:       'BRL',
                        payment_type:   'pix',
                        items: budgets.map((b, i) => ({
                          item_id:       String(b.travel_id || b.tour_id || b.id || i),
                          item_name:     b.travel?.[0]?.name || (b.travel_type === 'transfer' ? 'Transfer' : 'Serviço'),
                          item_category: b.travel?.[0]?.region_name || '',
                          price:         Math.round((Number(b.adults_total_amount || 0) + Number(b.kids_total_amount || 0) || Number(b.price || 0)) * 100) / 100,
                          quantity:      (parseInt(b.adults_total) || 0) + (parseInt(b.kids_total) || 0) || 1,
                        })),
                      },
                    });
                  } catch (_) {}
                }
              })
              .catch(() => {});
          }, 5000);
        })
        .catch((e) => {
          showToast(e?.response?.data?.message || t('pix_error'));
          setStep('method');
        })
        .finally(() => setPayLoading(false));
    };

    const handleCardPay = () => {
      if (!cardForm.holderName || !cardForm.number || !cardForm.expiryMonth || !cardForm.expiryYear || !cardForm.ccv) {
        showToast(t('card_fill_all')); return;
      }
      if (!cardForm.postalCode || cardForm.postalCode.length < 4) {
        showToast(t('cep_required')); return;
      }
      if (!cardForm.addressNumber) {
        showToast(t('address_number_required')); return;
      }
      if (cepStatus === 'manual' && !cardForm.address.trim()) {
        showToast(t('street_required')); return;
      }
      const expMonth = parseInt(cardForm.expiryMonth, 10);
      const expYear  = parseInt(cardForm.expiryYear, 10);
      const now = new Date();
      if (expMonth < 1 || expMonth > 12) { showToast(t('invalid_month')); return; }
      if (expYear < now.getFullYear() || (expYear === now.getFullYear() && expMonth < now.getMonth() + 1)) {
        showToast(t('card_expired')); return;
      }
      setCardRefusalMsg(null);
      setPayLoading(true);
      // Antifraude sem atrito: token Turnstile invisível (se configurado) + dados do dispositivo (3DS)
      getTurnstileToken()
        .then((turnstileToken) =>
          apiClient.httpInstance.post(`/customer/presales/${sale_id}/pay?access_token=${token}`, {
            payment_method: 'credit_card',
            total: totalCard,
            installments,
            card: {
              holderName: cardForm.holderName,
              number: cardForm.number.replace(/\D/g, ''),
              expiryMonth: cardForm.expiryMonth,
              expiryYear: cardForm.expiryYear,
              ccv: cardForm.ccv,
            },
            card_holder: {
              postalCode: cardForm.postalCode,
              addressNumber: cardForm.addressNumber,
              address: cardForm.address,
              province: cardForm.province,
            },
            turnstile_token: turnstileToken,
            turnstile_client: getTurnstileStatus(),
            device_info: getDeviceInfo(),
            return_url: window.location.href,
          }),
        )
        .then((r) => {
          if (r.data?.already_paid) {
            setStep('success');
            return;
          }
          // 3D Secure: o banco pediu confirmação — leva o cliente à tela do banco; ele volta para esta página
          if (r.data?.three_ds_challenge_url) {
            window.location.href = r.data.three_ds_challenge_url;
            return;
          }
          if (r.data?.paid) {
            setStep('success');
            // GA4: purchase — cartão confirmado
            try {
              window.dataLayer = window.dataLayer || [];
              window.dataLayer.push({ ecommerce: null });
              window.dataLayer.push({
                event: 'purchase',
                ecommerce: {
                  transaction_id: String(voucher?.references || voucher?.id || sale_id),
                  value:          Math.round(totalCard * 100) / 100,
                  currency:       'BRL',
                  payment_type:   'credit_card',
                  installments,
                  items: budgets.map((b, i) => ({
                    item_id:       String(b.travel_id || b.tour_id || b.id || i),
                    item_name:     b.travel?.[0]?.name || (b.travel_type === 'transfer' ? 'Transfer' : 'Serviço'),
                    item_category: b.travel?.[0]?.region_name || '',
                    price:         Math.round((Number(b.adults_total_amount || 0) + Number(b.kids_total_amount || 0) || Number(b.price || 0)) * 100) / 100,
                    quantity:      (parseInt(b.adults_total) || 0) + (parseInt(b.kids_total) || 0) || 1,
                  })),
                },
              });
            } catch (_) {}
          } else showToast(t('payment_not_confirmed'));
        })
        .catch((e) => {
          const msg = e?.response?.data?.message || t('payment_error');
          setCardRefusalMsg(msg);
        })
        .finally(() => setPayLoading(false));
    };

    React.useEffect(() => {
      return () => { if (pollRefLocal.current) clearInterval(pollRefLocal.current); };
    }, []);

    const installOpts = method === 'credit_card' ? buildInstallments(totalCard) : [];
    const bestFreeInst = installOpts.find(o => !o.hasInterest && o.n > 1) || installOpts[installOpts.length > 1 ? installOpts.filter(o => !o.hasInterest).length - 1 : 0];

    const inputStyle = {
      background: C.cardInner, border: `1px solid ${C.sep}`, borderRadius: 8,
      color: C.value, fontSize: 14, padding: '10px 12px', width: '100%',
      boxSizing: 'border-box', outline: 'none', marginBottom: 10,
    };

    if (!showPayModal) return null;

    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(5,12,25,0.92)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        padding: '0',
      }} onClick={(e) => { if (e.target === e.currentTarget && step !== 'pix' && step !== 'success') setShowPayModal(false); }}>
        <div style={{
          background: C.card, borderRadius: '20px 20px 0 0',
          width: '100%', maxWidth: 520,
          maxHeight: '92vh', overflowY: 'auto',
          padding: '24px 20px 32px',
          boxShadow: '0 -8px 40px rgba(0,0,0,0.5)',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <span style={{ color: C.accent, fontWeight: 800, fontSize: 16 }}>
              {step === 'cpf'     && t('step_id')}
              {step === 'method'  && t('step_method')}
              {step === 'dates'   && t('step_dates')}
              {step === 'pix'     && t('step_pix')}
              {step === 'card'    && t('step_card')}
              {step === 'ext_pay' && t('step_ext_pay')}
              {step === 'success' && t('step_success')}
            </span>
            {step !== 'success' && (
              <button onClick={() => { if (pollRefLocal.current) clearInterval(pollRefLocal.current); setShowPayModal(false); }}
                style={{ background: 'transparent', border: 'none', color: C.label, fontSize: 22, cursor: 'pointer', padding: '0 4px' }}>×</button>
            )}
          </div>

          {paySettingsLoading && (
            <div style={{ textAlign: 'center', padding: '40px 0', color: C.label }}>
              <CircularProgress size={28} style={{ color: C.accent }} />
              <p style={{ marginTop: 12, fontSize: 13 }}>{t('loading_payment_options')}</p>
            </div>
          )}

          {/* ── STEP: method ── */}
          {/* ── Step: CPF ── */}
          {step === 'cpf' && (
            <div style={{ padding: '8px 0' }}>
              <p style={{ color: C.label, fontSize: 14, marginBottom: 20, lineHeight: 1.6 }}>
                {t('cpf_intro')}
              </p>
              <label style={{ color: C.label, fontSize: 12, display: 'block', marginBottom: 6 }}>{t('cpf_label')}</label>
              <input
                type="text"
                inputMode="numeric"
                placeholder="000.000.000-00"
                value={cpfValue}
                onChange={(e) => setCpfValue(formatCpf(e.target.value))}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveCpf(); }}
                style={{
                  width: '100%', boxSizing: 'border-box',
                  background: C.cardInner,
                  border: `1px solid ${cpfShowError ? '#f87171' : C.sep}`,
                  borderRadius: 10, padding: '13px 14px',
                  color: C.value, fontSize: 16, outline: 'none',
                  marginBottom: cpfShowError ? 6 : 20, letterSpacing: 1,
                }}
                autoFocus
              />
              {cpfShowError && (
                <p style={{ color: '#f87171', fontSize: 12, margin: '0 0 16px', lineHeight: 1.4 }}>
                  {cpfMsg}
                </p>
              )}
              <button
                onClick={handleSaveCpf}
                disabled={cpfLoading || !cpfIsValid(cpfDigits)}
                style={{
                  width: '100%', background: C.approveGrad, color: '#fff',
                  border: 'none', borderRadius: 12, padding: '14px 0',
                  fontWeight: 800, fontSize: 15, cursor: (cpfLoading || !cpfIsValid(cpfDigits)) ? 'not-allowed' : 'pointer',
                  opacity: !cpfIsValid(cpfDigits) ? 0.5 : 1,
                }}
              >
                {cpfLoading ? t('saving') : t('continue_arrow')}
              </button>
            </div>
          )}

          {!paySettingsLoading && step === 'method' && (
            <div>
              {/* Resumo por item */}
              {items.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  {items.map((item) => (
                    <div key={item.budget_id} style={{
                      background: C.cardInner, borderRadius: 10, padding: '10px 14px', marginBottom: 8,
                      fontSize: 13, color: C.label,
                    }}>
                      <div style={{ color: C.value, fontWeight: 600, marginBottom: 4 }}>
                        {item.tour_name || (item.travel_type === 'transfer' ? 'Transfer' : 'Serviço')}
                        {item.region && <span style={{ color: C.muted, fontWeight: 400 }}> · {item.region}</span>}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>{t('subtotal')}: {fmtR(item.subtotal)}</span>
                        {item.pix_discount_pct > 0 && (
                          <span style={{ color: '#68D391' }}>
                            PIX {item.pix_discount_pct}% off · Card {item.card_discount_pct}% off
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── EN: USD direto, sem seletor ── */}
              {plang === 'en' && (
                <div style={{ marginBottom: 20, background: C.cardInner, borderRadius: 14, padding: '20px 16px', border: `1.5px solid ${C.accent}`, textAlign: 'center' }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>🇺🇸</div>
                  {intlAmount ? (
                    <>
                      <div style={{ color: C.label, fontSize: 12, marginBottom: 4 }}>{t('pay_currency_label')}</div>
                      <div style={{ color: C.accent, fontWeight: 800, fontSize: 28, marginBottom: 4 }}>
                        USD {Number(intlAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      {intlRate && (
                        <div style={{ color: C.muted, fontSize: 12 }}>
                          1 USD = R$ {Number(intlRate).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      )}
                    </>
                  ) : (
                    <CircularProgress size={24} style={{ color: C.accent }} />
                  )}
                </div>
              )}

              {/* ── ES: seletor LatAm (ARS/CLP/COP/MXN) ── */}
              {plang === 'es' && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ color: C.label, fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 10 }}>
                    {t('pay_currency_label')}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                    {[
                      { code: 'ARS', flag: '🇦🇷', label: 'ARS — Argentina' },
                      { code: 'CLP', flag: '🇨🇱', label: 'CLP — Chile' },
                      { code: 'COP', flag: '🇨🇴', label: 'COP — Colombia' },
                      { code: 'MXN', flag: '🇲🇽', label: 'MXN — México' },
                    ].map(({ code, flag, label }) => {
                      const active = intlCurrency === code;
                      return (
                        <button key={code} onClick={() => setIntlCurrency(code)} style={{
                          borderRadius: 10, padding: '10px 16px', cursor: 'pointer',
                          border: `1.5px solid ${active ? C.accent : C.sep}`,
                          background: active ? '#0d1e2a' : C.cardInner,
                          color: active ? C.accent : C.value,
                          fontSize: 13, fontWeight: active ? 700 : 400,
                          display: 'flex', alignItems: 'center', gap: 6,
                        }}>
                          <span>{flag}</span>
                          <span>{label}</span>
                        </button>
                      );
                    })}
                  </div>
                  {/* Caixa de cotação — aparece após selecionar */}
                  {intlCurrency && (
                    <div style={{ background: C.cardInner, borderRadius: 14, padding: '18px 16px', border: `1.5px solid ${C.accent}`, textAlign: 'center' }}>
                      {intlAmount ? (
                        <>
                          <div style={{ color: C.label, fontSize: 12, marginBottom: 4 }}>{t('pay_currency_label')}</div>
                          <div style={{ color: C.accent, fontWeight: 800, fontSize: 28, marginBottom: 4 }}>
                            {intlCurrency} {Number(intlAmount).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </div>
                          {intlRate && (
                            <div style={{ color: C.muted, fontSize: 12 }}>
                              1 {intlCurrency} = R$ {Number(intlRate).toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                            </div>
                          )}
                        </>
                      ) : (
                        <CircularProgress size={24} style={{ color: C.accent }} />
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Cards PIX / Cartão */}
              {plang === 'pt' && <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
                {/* PIX */}
                <div onClick={() => { setMethod('pix'); trackEvent('checkout_method', 'PIX'); }} style={{
                  flex: 1, borderRadius: 14, padding: '16px 14px', cursor: 'pointer',
                  border: `2px solid ${method === 'pix' ? '#68D391' : C.sep}`,
                  background: method === 'pix' ? '#0d2a1e' : C.cardInner,
                  transition: 'all 0.15s',
                }}>
                  <div style={{ fontSize: 22, marginBottom: 6 }}>⚡</div>
                  <div style={{ color: C.value, fontWeight: 700, fontSize: 14, marginBottom: 4 }}>PIX</div>
                  {totals.bruto > totalPix && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                      <span style={{ color: '#FACC15', fontSize: 14, fontWeight: 600, textDecoration: 'line-through', textDecorationThickness: 1, textDecorationColor: 'rgba(250,204,21,.45)' }}>{t('of')} {fmtR(totals.bruto)}</span>
                      <span style={{ background: '#FACC15', color: '#412402', borderRadius: 20, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>-{pixBadgePct ?? Math.round((1 - totalPix / totals.bruto) * 100)}%</span>
                    </div>
                  )}
                  <div style={{ color: '#68D391', fontWeight: 800, fontSize: 18 }}>{totals.bruto > totalPix ? `${t('per')} ` : ''}{fmtR(totalPix)}</div>
                  <div style={{ color: C.label, fontSize: 11, marginTop: 4 }}>{t('instant_payment')}</div>
                  {totals.bruto > totalPix && (
                    <div style={{ color: '#68D391', fontSize: 11, marginTop: 2, fontWeight: 600 }}>
                      {t('pix_economy')} {fmtR(round2(totals.bruto - totalPix))}
                    </div>
                  )}
                </div>
                {/* Cartão — oculto para Monteluz */}
                {!hasMonteluzCurrentMonth && <div onClick={() => { setMethod('credit_card'); trackEvent('checkout_method', 'Cartão'); }} style={{
                  flex: 1, borderRadius: 14, padding: '16px 14px', cursor: 'pointer',
                  border: `2px solid ${method === 'credit_card' ? C.accent : C.sep}`,
                  background: method === 'credit_card' ? '#0d1e2a' : C.cardInner,
                  transition: 'all 0.15s',
                }}>
                  <div style={{ fontSize: 22, marginBottom: 6 }}>💳</div>
                  <div style={{ color: C.value, fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{t('card_label_short').replace('💳 ', '')}</div>
                  {totals.bruto > totalCard && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                      <span style={{ color: '#FACC15', fontSize: 14, fontWeight: 600, textDecoration: 'line-through', textDecorationThickness: 1, textDecorationColor: 'rgba(250,204,21,.45)' }}>{t('of')} {fmtR(totals.bruto)}</span>
                      <span style={{ background: '#FACC15', color: '#412402', borderRadius: 20, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>-{cardBadgePct ?? Math.round((1 - totalCard / totals.bruto) * 100)}%</span>
                    </div>
                  )}
                  <div style={{ color: C.accent, fontWeight: 800, fontSize: 18 }}>{totals.bruto > totalCard ? `${t('per')} ` : ''}{fmtR(totalCard)}</div>
                  {installOpts.length > 0 && bestFreeInst && bestFreeInst.n > 1 && (
                    <div style={{ color: C.label, fontSize: 11, marginTop: 4 }}>
                      {t('or')} {bestFreeInst.n}{t('x_of')} {fmtR(bestFreeInst.val)} {t('no_interest')}
                    </div>
                  )}
                  {totals.bruto > totalCard && (
                    <div style={{ color: C.accent, fontSize: 11, marginTop: 2, fontWeight: 600 }}>
                      {t('card_economy')} {fmtR(round2(totals.bruto - totalCard))}
                    </div>
                  )}
                </div>}
              </div>}

              {/* ── Cards PIX split (30/50/100%) — só quando PIX selecionado e moeda BRL ── */}
              {method === 'pix' && plang === 'pt' && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ color: C.label, fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 10 }}>
                    {t('how_pay_pix')}
                  </div>
                  {[
                    { pct: 30, badge: t('most_chosen'), title: t('secure_spot_with') },
                    { pct: 50, badge: null,             title: t('down_50') },
                    { pct: 100, badge: null,             title: t('full_payment') },
                  ].filter(({ pct }) => !hasMonteluzCurrentMonth || pct === 100)
                  .map(({ pct, badge, title }) => {
                    const nowAmt  = Math.round(totalPix * pct / 100 * 100) / 100;
                    const restAmt = Math.round((totalPix - nowAmt) * 100) / 100;
                    const active  = pixSplit === pct;
                    if (nowAmt < (minVal || 5) && pct < 100) return null;

                    // Card compacto (não selecionado)
                    if (!active) return (
                      <div key={pct} onClick={() => setPixSplit(pct)} style={{
                        borderRadius: 14, padding: '14px 16px', cursor: 'pointer', marginBottom: 8,
                        border: `1.5px solid ${C.sep}`, background: C.cardInner,
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        transition: 'all 0.15s',
                      }}>
                        <div>
                          <div style={{ color: C.value, fontWeight: 600, fontSize: 14 }}>{title}</div>
                          <div style={{ color: C.muted, fontSize: 12, marginTop: 2 }}>
                            {pct < 100 ? t('balance_48h') : t('confirmed_immediately')}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', minWidth: 90 }}>
                          <div style={{ color: C.value, fontWeight: 800, fontSize: 16 }}>{fmtR(nowAmt)}</div>
                          {pct < 100
                            ? <div style={{ color: '#68D391', fontSize: 11, marginTop: 2 }}>+ {fmtR(restAmt)} {t('plus_later')}</div>
                            : <div style={{ color: C.muted, fontSize: 11, marginTop: 2 }}>{t('nothing_more')}</div>
                          }
                        </div>
                      </div>
                    );

                    // Card expandido (selecionado)
                    return (
                      <div key={pct} style={{
                        borderRadius: 14, padding: '18px 16px', marginBottom: 8,
                        border: `2px solid #68D391`, background: '#071e14',
                        transition: 'all 0.15s',
                      }}>
                        {badge && (
                          <span style={{
                            background: '#68D391', color: '#052e16', borderRadius: 20,
                            padding: '3px 12px', fontSize: 10, fontWeight: 700, marginBottom: 10, display: 'inline-block',
                          }}>{badge}</span>
                        )}
                        <div style={{ color: C.label, fontSize: 13, marginBottom: 4 }}>{title}</div>
                        <div style={{ color: '#68D391', fontWeight: 900, fontSize: 28, marginBottom: 4 }}>{fmtR(nowAmt)}</div>
                        <div style={{ color: C.muted, fontSize: 12, marginBottom: pct < 100 ? 12 : 0 }}>
                          {t('today_confirmed')}
                        </div>
                        {pct < 100 && (
                          <div style={{
                            background: '#0d2a1e', borderRadius: 10, padding: '10px 12px',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10,
                          }}>
                            <div>
                              <div style={{ color: C.label, fontSize: 11 }}>{t('remaining')}</div>
                              <div style={{ color: C.value, fontWeight: 700, fontSize: 15 }}>{fmtR(restAmt)}</div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ color: C.label, fontSize: 11 }}>{t('paid_48h_before')}</div>
                              <div style={{ color: C.muted, fontSize: 11 }}>{t('of_boarding')}</div>
                            </div>
                          </div>
                        )}
                        {totals.bruto > totalPix && (
                          <div style={{ background: '#0a2819', borderRadius: 8, padding: '8px 12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2, flexWrap: 'wrap' }}>
                              <span style={{ color: '#FACC15', fontSize: 16, fontWeight: 600, textDecoration: 'line-through', textDecorationThickness: 1, textDecorationColor: 'rgba(250,204,21,.45)' }}>{t('of')} {fmtR(totals.bruto)}</span>
                              <span style={{ background: '#FACC15', color: '#412402', borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 700 }}>-{pixBadgePct ?? Math.round((1 - totalPix / totals.bruto) * 100)}%</span>
                              <span style={{ color: '#68D391', fontWeight: 900, fontSize: 17 }}>{t('per')} {fmtR(totalPix)} {t('in_pix')}</span>
                            </div>
                            <div style={{ color: '#68D391', fontSize: 12, fontWeight: 600 }}>
                              {t('you_save')} {fmtR(round2(totals.bruto - totalPix))}{transferNote}
                            </div>
                            {managerDiscount > 0 && (
                              <div style={{ color: '#c4b5fd', fontSize: 11, fontWeight: 600, marginTop: 3 }}>
                                {t('manager_discount_incl')} {voucher?.approval_type === 'discount_pct' ? `${voucher.approval_manager_value}% — ` : ''}{fmtR(managerDiscount)}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div style={{ textAlign: 'center', color: C.muted, fontSize: 11, marginTop: 6 }}>
                    {t('trust_line')}
                  </div>
                </div>
              )}

              {/* ── Botão confirmar — EN/ES usam fluxo externo, PT nunca chega aqui ── */}
              {plang !== 'pt' ? (
                <button
                  onClick={createExtPayLink}
                  disabled={!intlCurrency || !intlAmount || payLinkLoading}
                  style={{
                    width: '100%',
                    background: (intlCurrency && intlAmount) ? C.approveGrad : C.sep,
                    color: '#fff', border: 'none', borderRadius: 12,
                    padding: '16px 0', fontWeight: 800, fontSize: 15,
                    cursor: (intlCurrency && intlAmount && !payLinkLoading) ? 'pointer' : 'not-allowed',
                    opacity: payLinkLoading ? 0.7 : 1,
                    marginTop: 8,
                  }}>
                  {payLinkLoading
                    ? t('ext_pay_opening')
                    : !intlCurrency
                      ? t('pay_currency_label')
                      : !intlAmount
                        ? '...'
                        : `${t('confirm_and_pay_usd')} ${intlCurrency} ${Number(intlAmount).toLocaleString('en-US', { minimumFractionDigits: plang === 'es' ? 0 : 2, maximumFractionDigits: plang === 'es' ? 0 : 2 })} →`
                  }
                </button>
              ) : (
                <button
                  onClick={handleConfirmMethod}
                  disabled={!method || availLoading}
                  style={{
                    width: '100%', background: method ? C.approveGrad : C.sep,
                    color: '#fff', border: 'none', borderRadius: 12,
                    padding: '16px 0', fontWeight: 800, fontSize: 15,
                    cursor: method ? 'pointer' : 'not-allowed',
                    opacity: availLoading ? 0.7 : 1,
                  }}>
                  {availLoading ? t('checking_dates') : method === 'pix' ? `${t('generate_pix')} ${fmtR(chargeAmount)}` : t('continue_arrow')}
                </button>
              )}
            </div>
          )}

          {/* ── STEP: dates ── */}
          {step === 'dates' && availability && (
            <div>
              <p style={{ color: C.label, fontSize: 13, marginBottom: 14, lineHeight: 1.6 }}>
                {t('dates_intro')}
              </p>
              <div style={{ marginBottom: 20 }}>
                {availability.items.map((item) => (
                  <div key={item.budget_id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '12px 14px', borderRadius: 10, marginBottom: 8,
                    background: item.ok ? '#0d2a1e' : '#2a0d0d',
                    border: `1px solid ${item.ok ? '#2a5c42' : '#5c2a2a'}`,
                  }}>
                    <div>
                      <div style={{ color: C.value, fontWeight: 600, fontSize: 13 }}>{item.tour_name}</div>
                      <div style={{ color: C.label, fontSize: 12 }}>
                        {item.tour_date
                          ? new Date(item.tour_date + 'T00:00:00').toLocaleDateString('pt-BR')
                          : <span style={{ color: C.danger }}>{t('no_date_selected')}</span>}
                        {item.tour_date && ` · ${item.needed} ${item.needed !== 1 ? t('people') : t('person')}`}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      {item.ok && <span style={{ color: '#68D391', fontWeight: 700, fontSize: 13 }}>{t('avail_ok')}</span>}
                      {item.no_date && <span style={{ color: C.danger, fontWeight: 700, fontSize: 12 }}>{t('choose_a_date')}</span>}
                      {item.no_spots && !item.no_date && (
                        <div>
                          <span style={{ color: C.danger, fontWeight: 700, fontSize: 12 }}>{t('no_spots')}</span>
                          <div style={{ color: C.label, fontSize: 11 }}>
                            {item.available != null ? `${item.available} ${t('available_count')}` : t('unavailable')}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {!availability.all_ok && (
                <div style={{
                  background: '#2a1a0d', borderRadius: 10, padding: '12px 14px',
                  color: '#F6AD55', fontSize: 13, marginBottom: 16, lineHeight: 1.5,
                }}>
                  {t('dates_warning')}
                </div>
              )}

              {/* ── Seletor de moeda no step dates (apenas EN/ES) ── */}
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setShowPayModal(false)} style={{
                  flex: 1, background: 'transparent', color: C.label,
                  border: `1px solid ${C.sep}`, borderRadius: 12,
                  padding: '14px 0', fontWeight: 600, fontSize: 14, cursor: 'pointer',
                }}>{t('back')}</button>
                <button
                  onClick={availability.all_ok ? handleProceedFromDates : () => setShowPayModal(false)}
                  style={{
                    flex: 2, background: availability.all_ok ? C.approveGrad : '#b45309',
                    color: '#fff', border: 'none', borderRadius: 12,
                    padding: '14px 0', fontWeight: 800, fontSize: 14, cursor: 'pointer',
                  }}>
                  {availability.all_ok ? t('confirm_and_pay') : t('close_adjust_dates')}
                </button>
              </div>
            </div>
          )}

          {/* ── STEP: pix ── */}
          {step === 'pix' && (
            <div style={{ textAlign: 'center' }}>
              {payLoading && (
                <div style={{ padding: '40px 0', color: C.label }}>
                  <CircularProgress size={28} style={{ color: C.accent }} />
                  <p style={{ marginTop: 12, fontSize: 13 }}>{t('generating_qr')}</p>
                </div>
              )}
              {!payLoading && pixData && (
                <div>
                  <div style={{
                    background: '#fff', borderRadius: 14, padding: 12,
                    display: 'inline-block', marginBottom: 16,
                  }}>
                    <img src={`data:image/png;base64,${pixData.qr_code_image}`}
                      alt="QR Code PIX"
                      style={{ width: 200, height: 200, display: 'block' }} />
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ color: C.label, fontSize: 12, marginBottom: 6 }}>{t('or_copy_code')}</div>
                    <div style={{
                      background: C.cardInner, borderRadius: 8, padding: '10px 12px',
                      fontSize: 11, color: C.value, wordBreak: 'break-all',
                      border: `1px solid ${C.sep}`, maxHeight: 60, overflow: 'hidden',
                    }}>{pixData.copy_paste}</div>
                    <button onClick={() => { navigator.clipboard?.writeText(pixData.copy_paste); showToast(t('code_copied'), 'success'); }}
                      style={{
                        marginTop: 8, background: C.cardInner,
                        color: C.accent, border: `1px solid ${C.accent}`,
                        borderRadius: 8, padding: '8px 20px', fontSize: 13,
                        fontWeight: 700, cursor: 'pointer',
                      }}>{t('copy_code')}</button>
                  </div>
                  {pixSplit < 100 ? (
                    <div style={{ background: C.cardInner, borderRadius: 10, padding: '12px 16px', marginBottom: 16, textAlign: 'left' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <span style={{ color: C.label, fontSize: 12 }}>{t('paying_now')}</span>
                        <span style={{ color: '#68D391', fontWeight: 800, fontSize: 16 }}>{fmtR(chargeAmount)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: C.label, fontSize: 12 }}>{t('remaining_hour')}</span>
                        <span style={{ color: C.value, fontSize: 14 }}>{fmtR(restAmount)}</span>
                      </div>
                      <div style={{ color: C.muted, fontSize: 11, marginTop: 6 }}>
                        {t('balance_note')}
                      </div>
                    </div>
                  ) : (
                    <div style={{ color: '#68D391', fontWeight: 700, fontSize: 18, marginBottom: 4 }}>{fmtR(chargeAmount)}</div>
                  )}
                  <div style={{ color: C.label, fontSize: 12, marginBottom: 20 }}>
                    {t('waiting_confirmation')}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: C.muted, fontSize: 12 }}>
                    <CircularProgress size={12} style={{ color: C.muted }} />
                    {t('auto_check_5s')}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── STEP: card travado — precisa de autorização gerencial ou Pix (2026-10-21) ── */}
          {step === 'card' && cardLocked && (
            <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
              <div style={{ fontSize: 30, marginBottom: 6 }}>🔐</div>
              <div style={{ color: C.value, fontWeight: 800, fontSize: 17, marginBottom: 10 }}>
                {holdState === 'pending'
                  ? HL('Pedido enviado', 'Request sent', 'Solicitud enviada')
                  : holdState === 'rejected'
                    ? HL('Pague com Pix', 'Pay with Pix', 'Paga con Pix')
                    : HL('Confirmação rápida da sua compra', 'Quick confirmation of your purchase', 'Confirmación rápida de tu compra')}
              </div>
              <div style={{ color: C.label, fontSize: 14, lineHeight: 1.5, marginBottom: 18 }}>
                {holdState === 'pending'
                  ? HL('Recebemos o seu pedido. Nossa equipe vai confirmar e avisar você por e-mail. Se preferir, pague agora no Pix.',
                       'We received your request. Our team will confirm and email you. If you prefer, you can pay now with Pix.',
                       'Recibimos tu solicitud. Nuestro equipo la confirmará y te avisará por e-mail. Si prefieres, paga ahora con Pix.')
                  : holdState === 'rejected'
                    ? HL('Para concluir este orçamento, finalize o pagamento no Pix.',
                         'To complete this quote, please pay with Pix.',
                         'Para completar este presupuesto, realiza el pago con Pix.')
                    : HL('Para a sua segurança, o pagamento com cartão deste orçamento precisa de autorização da nossa equipe. Você pode pedir a liberação do cartão ou pagar agora no Pix.',
                         'For your security, card payment for this quote needs authorization from our team. You can request card release or pay now with Pix.',
                         'Por tu seguridad, el pago con tarjeta de este presupuesto necesita autorización de nuestro equipo. Puedes solicitar la liberación de la tarjeta o pagar ahora con Pix.')}
              </div>
              <button onClick={() => { setMethod('pix'); setStep('method'); }} style={{
                width: '100%', background: C.approveGrad, color: '#fff', border: 'none', borderRadius: 12,
                padding: '14px 0', fontWeight: 800, fontSize: 15, cursor: 'pointer', marginBottom: 10,
              }}>{HL('Pagar agora no Pix', 'Pay now with Pix', 'Pagar ahora con Pix')}</button>
              {holdState === 'needs_release' && (
                <button onClick={requestCardRelease} disabled={holdLoading} style={{
                  width: '100%', background: 'transparent', color: C.value, border: `1.5px solid ${C.sep}`, borderRadius: 12,
                  padding: '13px 0', fontWeight: 700, fontSize: 14, cursor: holdLoading ? 'not-allowed' : 'pointer', opacity: holdLoading ? 0.7 : 1,
                }}>{holdLoading ? t('processing') : HL('Pedir liberação do cartão', 'Request card release', 'Solicitar liberación de la tarjeta')}</button>
              )}
              {holdState === 'pending' && (
                <button onClick={refreshCardHold} disabled={holdLoading} style={{
                  width: '100%', background: 'transparent', color: C.value, border: `1.5px solid ${C.sep}`, borderRadius: 12,
                  padding: '13px 0', fontWeight: 700, fontSize: 14, cursor: holdLoading ? 'not-allowed' : 'pointer', opacity: holdLoading ? 0.7 : 1,
                }}>{HL('Atualizar status', 'Refresh status', 'Actualizar estado')}</button>
              )}
              <button onClick={() => setStep('method')} style={{
                marginTop: 10, background: 'transparent', color: C.label, border: 'none', fontSize: 13, cursor: 'pointer',
              }}>{t('back')}</button>
            </div>
          )}

          {/* ── STEP: card ── */}
          {step === 'card' && !cardLocked && (
            <div>
              {/* ── Parcelamento — grid igual ao FTApp ── */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div style={{ color: C.label, fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase' }}>
                    {t('installments_title')}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: C.label, fontSize: 11 }}>
                      <span style={{ width: 7, height: 7, borderRadius: 4, background: '#68D391', display: 'inline-block' }} /> {t('no_interest')}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: C.label, fontSize: 11 }}>
                      <span style={{ width: 7, height: 7, borderRadius: 4, background: '#F6AD55', display: 'inline-block' }} /> {t('with_interest')}
                    </span>
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                  {installOpts.map((opt) => {
                    const sel = installments === opt.n;
                    const green = !opt.hasInterest;
                    return (
                      <button key={opt.n} onClick={() => setInstallments(opt.n)} style={{
                        borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600,
                        cursor: 'pointer', border: `1.5px solid`,
                        borderColor: sel ? (green ? '#68D391' : '#F6AD55') : (green ? 'rgba(104,211,145,0.35)' : 'rgba(246,173,85,0.30)'),
                        background: sel ? (green ? 'rgba(104,211,145,0.18)' : 'rgba(246,173,85,0.18)') : (green ? 'rgba(104,211,145,0.06)' : 'rgba(246,173,85,0.05)'),
                        color: sel ? '#fff' : (green ? '#86efac' : '#fcd34d'),
                      }}>
                        {opt.n}x
                      </button>
                    );
                  })}
                </div>
                <div style={{
                  borderRadius: 10, padding: '10px 14px', textAlign: 'center',
                  border: `1px solid`,
                  borderColor: installments <= maxFree ? 'rgba(104,211,145,0.25)' : 'rgba(246,173,85,0.30)',
                  background: installments <= maxFree ? 'rgba(104,211,145,0.08)' : 'rgba(246,173,85,0.08)',
                  color: installments <= maxFree ? '#68D391' : '#F6AD55',
                  fontWeight: 700, fontSize: 14,
                }}>
                  {installments === 1
                    ? `${fmtR(totalCard)} ${t('cash')} · ${t('no_interest')}`
                    : installments <= maxFree
                      ? `${installments}${t('x_of')} ${fmtR(calcInstallmentValue(totalCard, installments))} · ${t('no_interest')}`
                      : `${installments}${t('x_of')} ${fmtR(calcInstallmentValue(totalCard, installments))} · ${t('total_word')} ${fmtR(round2(calcInstallmentValue(totalCard, installments) * installments))} (${rate}% a.m.)`}
                </div>
              </div>

              {/* ── Dados do cartão ── */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ color: C.label, fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 10 }}>
                  {t('card_data')}
                </div>
                <input style={inputStyle} placeholder={t('name_on_card')}
                  value={cardForm.holderName}
                  onChange={(e) => setCardForm(f => ({ ...f, holderName: e.target.value }))}
                  autoCapitalize="characters" />
                <input style={inputStyle} placeholder={t('card_number')}
                  value={cardForm.number}
                  onChange={(e) => setCardForm(f => ({ ...f, number: e.target.value.replace(/\D/g, '').slice(0, 16) }))}
                  inputMode="numeric" maxLength={16} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <input style={{ ...inputStyle, flex: 1, marginBottom: 0 }} placeholder="MM/AA"
                    value={cardForm.expiryYear
                      ? `${cardForm.expiryMonth}/${cardForm.expiryYear.replace(/^20/, '')}`
                      : cardForm.expiryMonth}
                    onChange={(e) => {
                      const raw = e.target.value;
                      let m, y;
                      if (raw.includes('/')) {
                        const parts = raw.split('/');
                        m = parts[0].replace(/\D/g, '').slice(0, 2);
                        if (m.length === 1 && parseInt(m, 10) > 1) m = '0' + m;
                        y = (parts[1] || '').replace(/\D/g, '').slice(0, 2);
                      } else {
                        const d = raw.replace(/\D/g, '').slice(0, 4);
                        m = d.slice(0, 2);
                        y = d.slice(2);
                      }
                      setCardForm(f => ({ ...f, expiryMonth: m, expiryYear: y ? '20' + y : '' }));
                    }}
                    inputMode="numeric" maxLength={5} autoComplete="cc-exp" />
                  <input style={{ ...inputStyle, flex: 1, marginBottom: 0 }} placeholder="CVV"
                    value={cardForm.ccv}
                    onChange={(e) => setCardForm(f => ({ ...f, ccv: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                    inputMode="numeric" maxLength={4} type="password" />
                </div>
              </div>

              {/* ── Endereço de cobrança ── */}
              <div style={{ marginBottom: 16, background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: '12px 14px', border: `1px solid ${C.sep}` }}>
                <div style={{ color: C.label, fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 10 }}>
                  {t('billing_address')} <span style={{ color: '#f87171', fontSize: 10, fontWeight: 600 }}>{t('required')}</span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input style={{ ...inputStyle, flex: 2, marginBottom: 0 }} placeholder={t('cep_placeholder')}
                    value={cardForm.postalCode}
                    onChange={(e) => onPostalChange(e.target.value)}
                    autoComplete="postal-code" />
                  <input style={{ ...inputStyle, flex: 1, marginBottom: 0 }} placeholder={t('number_placeholder')}
                    value={cardForm.addressNumber}
                    onChange={(e) => setCardForm(f => ({ ...f, addressNumber: e.target.value }))} />
                </div>
                {cepStatus === 'loading' && (
                  <div style={{ color: C.label, fontSize: 12, marginTop: 8 }}>{t('cep_searching')}</div>
                )}
                {cepStatus === 'manual' && (
                  <div style={{ color: '#fbbf24', fontSize: 12, marginTop: 8 }}>{t('cep_manual_hint')}</div>
                )}
                {(cepStatus === 'found' || cepStatus === 'manual') && (
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <input style={{ ...inputStyle, flex: 2, marginBottom: 0 }} placeholder={t('street_placeholder')}
                    value={cardForm.address}
                    onChange={(e) => setCardForm(f => ({ ...f, address: e.target.value }))}
                    autoComplete="address-line1" />
                  <input style={{ ...inputStyle, flex: 1.4, marginBottom: 0 }} placeholder={t('district_placeholder')}
                    value={cardForm.province}
                    onChange={(e) => setCardForm(f => ({ ...f, province: e.target.value }))}
                    autoComplete="address-level2" />
                </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setStep('method')} style={{
                  flex: 1, background: 'transparent', color: C.label,
                  border: `1px solid ${C.sep}`, borderRadius: 12,
                  padding: '14px 0', fontWeight: 600, fontSize: 14, cursor: 'pointer',
                }}>{t('back')}</button>
                <button onClick={handleCardPay} disabled={payLoading} style={{
                  flex: 2, background: C.approveGrad, color: '#fff',
                  border: 'none', borderRadius: 12,
                  padding: '14px 0', fontWeight: 800, fontSize: 15,
                  cursor: payLoading ? 'not-allowed' : 'pointer',
                  opacity: payLoading ? 0.7 : 1,
                }}>
                  {payLoading ? t('processing') : t('pay_with_card')}
                </button>
              </div>
              {cardRefusalMsg && (
                <div style={{
                  marginTop: 14, background: '#fef2f2', border: '1.5px solid #fca5a5',
                  borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'flex-start', gap: 10,
                }}>
                  <span style={{ fontSize: 18, lineHeight: 1 }}>❌</span>
                  <div>
                    <div style={{ fontWeight: 700, color: '#b91c1c', fontSize: 14 }}>Pagamento recusado</div>
                    <div style={{ color: '#dc2626', fontSize: 13, marginTop: 2 }}>{cardRefusalMsg}</div>
                    <div style={{ color: '#991b1b', fontSize: 12, marginTop: 6 }}>Verifique os dados e tente novamente, ou use outro cartão.</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── STEP: ext_pay — aguardando pagamento externo (Nautt/Aloha) ── */}
          {step === 'ext_pay' && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              {payLinkLoading ? (
                <div style={{ padding: '20px 0' }}>
                  <CircularProgress size={32} style={{ color: C.accent }} />
                  <p style={{ marginTop: 14, color: C.label, fontSize: 14 }}>{t('ext_pay_opening')}</p>
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 48, marginBottom: 14 }}>🌐</div>
                  <div style={{ color: C.value, fontWeight: 700, fontSize: 15, marginBottom: 10, lineHeight: 1.5 }}>
                    {t('ext_pay_opened')}
                  </div>
                  {payLinkUrl && (
                    <button
                      onClick={() => window.open(payLinkUrl, '_blank', 'noopener,noreferrer')}
                      style={{
                        background: C.approveGrad, color: '#fff',
                        border: 'none', borderRadius: 12,
                        padding: '14px 24px', fontWeight: 800, fontSize: 14,
                        cursor: 'pointer', marginBottom: 20, width: '100%',
                      }}
                    >{t('ext_pay_reopen')}</button>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: C.muted, fontSize: 12 }}>
                    <CircularProgress size={12} style={{ color: C.muted }} />
                    {t('ext_pay_waiting')}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── STEP: success ── */}
          {step === 'success' && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>✅</div>
              <div style={{ color: '#68D391', fontWeight: 800, fontSize: 20, marginBottom: 8 }}>
                {t('payment_confirmed')}
              </div>
              <div style={{ color: C.label, fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>
                {t('success_text')}
              </div>
              <button onClick={() => window.location.reload()} style={{
                background: C.approveGrad, color: '#fff',
                border: 'none', borderRadius: 12,
                padding: '16px 32px', fontWeight: 800, fontSize: 15,
                cursor: 'pointer',
              }}>{t('see_my_reservation')}</button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div style={{ background: C.pageBg, minHeight: '100vh', fontFamily: '"Segoe UI", system-ui, sans-serif', color: C.value }}>
      <Toast toast={toast} />
      {/* IMPORTANTE: chamar como função {PaymentModal()} — NÃO usar <PaymentModal/>.
          Como PaymentModal é definido inline, renderizar como componente faz o React
          remontá-lo a cada re-render do pai (polling/cotação/chat), perdendo o que o
          cliente digitou no cartão ("voltava ao início"). Chamando como função, os hooks
          dele entram na lista de hooks do pai e o estado persiste. Seguro aqui porque o pai
          não tem early-return e os hooks do modal ficam antes do `if (!showPayModal) return null`. */}
      {PaymentModal()}

      {/* Nav bar */}
      <header style={{
        background: C.navBg,
        height: 58,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'fixed',
        top: 0, width: '100%', zIndex: 100,
        boxShadow: '0 2px 12px rgba(0,0,0,0.4)',
        borderBottom: `1px solid ${C.sep}`,
      }}>
        {logo
          ? <img src={logo} alt="Full Tour" style={{ height: 36, maxWidth: 140, objectFit: 'contain' }} />
          : <span style={{ color: '#62DEC2', fontWeight: 800, fontSize: 22, letterSpacing: 2 }}>
              Full Tour
            </span>
        }
        {/* Seletor de idioma (Presale-1) */}
        <div style={{
          position: 'absolute', right: 14, top: 0, height: '100%',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          {[
            { l: 'pt', f: '🇧🇷' },
            { l: 'en', f: '🇺🇸' },
            { l: 'es', f: '🇪🇸' },
          ].map(({ l, f }) => (
            <button
              key={l}
              onClick={() => changeLang(l)}
              aria-label={l}
              style={{
                background: 'transparent', border: 'none', padding: '2px 3px',
                fontSize: 18, lineHeight: 1, cursor: 'pointer',
                opacity: plang === l ? 1 : 0.5,
                transform: plang === l ? 'scale(1.12)' : 'none',
                transition: 'opacity 0.15s, transform 0.15s',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              {f}
            </button>
          ))}
        </div>
      </header>

      {/* Responsive grid styles + scroll fix */}
      <style>{`
        *, *::before, *::after { box-sizing: border-box; }
        /* Desfaz o overflow:hidden + position:fixed que auth/app.css aplica globalmente */
        html, body { height: auto !important; overflow: auto !important; min-height: 100%; }
        .App { overflow: visible !important; position: relative !important; height: auto !important; }
        .presale-wrap { max-width: 1140px; margin: 0 auto; padding: 72px 14px 48px; }
        .presale-grid { display: flex; flex-direction: column; gap: 16px; }
        .presale-left { width: 100%; min-width: 0; }
        .presale-right { width: 100%; min-width: 0; }
        img { max-width: 100%; height: auto; }
        input, textarea, button { box-sizing: border-box; }
        @media (min-width: 769px) {
          .presale-grid { display: flex; flex-direction: row; gap: 22px; align-items: flex-start; margin-top: 16px; }
          .presale-left { flex: 1; }
          .presale-right { width: 360px; flex-shrink: 0; position: sticky; top: 74px; display: flex; flex-direction: column; margin-top: 0; }
          .presale-right-payment { flex-shrink: 0; }
          .presale-right-chat { height: 340px; overflow-y: auto; margin-top: 8px; }
          .presale-right-chat::-webkit-scrollbar { width: 4px; }
          .presale-right-chat::-webkit-scrollbar-thumb { background: #1a3050; border-radius: 2px; }
        }
      `}</style>

      {/* Main */}
      <div className="presale-wrap">

        {/* Loading */}
        {loading && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 80, gap: 16 }}>
            <CircularProgress style={{ color: C.accent }} size={40} />
            <span style={{ color: C.label, fontSize: 14 }}>{t('loading_budget')}</span>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div style={{
            background: C.card, borderRadius: 16, padding: 28,
            textAlign: 'center', color: C.danger, marginTop: 20,
          }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>😕</div>
            <div style={{ fontSize: 15, lineHeight: 1.6 }}>{t(error)}</div>
          </div>
        )}

        {/* Content */}
        {!loading && !error && voucher && (
          <>
            {/* ── BLACK FRIDAY (nov/2026): só em orçamento que tem direito à promoção e ainda pode ser pago ── */}
            {bfOn && (
              <div style={{
                background: '#000',
                borderRadius: 20,
                border: '2px solid #ec0808',
                boxShadow: '0 0 28px rgba(236,8,8,0.45), 0 8px 32px rgba(0,0,0,0.5)',
                padding: '14px 18px 20px',
                marginBottom: 16,
                textAlign: 'center',
                overflow: 'hidden',
              }}>
                <img
                  src={`${process.env.PUBLIC_URL || ''}/black-friday.gif`}
                  alt={t('bf_badge')}
                  style={{ display: 'block', margin: '0 auto', width: '100%', maxWidth: 340, height: 'auto' }}
                />
                <div style={{ color: '#fff', fontWeight: 800, fontSize: 18, lineHeight: 1.3, marginTop: 6 }}>
                  {t('bf_headline')}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 10, marginTop: 14 }}>
                  <div style={{ background: '#00ff36', color: '#000', borderRadius: 10, padding: '8px 14px', fontWeight: 800, fontSize: 15 }}>
                    ⚡ {Number(bfPromo.pix_pct)}% {t('bf_pix_label')}
                  </div>
                  <div style={{ background: '#fffc00', color: '#000', borderRadius: 10, padding: '8px 14px', fontWeight: 800, fontSize: 15 }}>
                    💳 {Number(bfPromo.card_pct)}% {t('bf_card_label')}
                  </div>
                </div>
                <div style={{ color: '#ffb3b3', fontSize: 13, lineHeight: 1.6, marginTop: 14 }}>
                  {t('bf_valid')} <strong style={{ color: '#fff' }}>{bfPromo.ends_label}</strong>.
                </div>
                <div style={{ color: '#9aa7b8', fontSize: 12, lineHeight: 1.5, marginTop: 4 }}>
                  {t('bf_note')}
                </div>
              </div>
            )}

            {/* ── HERO: saudação personalizada (full width) ── */}
            <div style={{
              background: `linear-gradient(135deg, #0f2a45 0%, #132035 100%)`,
              borderRadius: 20,
              padding: '24px 22px',
              border: `1px solid ${C.sep}`,
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontSize: 24, marginBottom: 6 }}>✈️</div>
                  <div style={{ color: C.value, fontSize: 20, fontWeight: 700, lineHeight: 1.3, marginBottom: 6 }}>
                    {t('hello')} {firstName}!
                  </div>
                  <div style={{ color: C.label, fontSize: 14, lineHeight: 1.6 }}>
                    {t('hero_text')}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 160 }}>
                  <div style={{
                    background: C.cardInner, borderRadius: 10, padding: '8px 14px',
                    display: 'flex', flexDirection: 'column',
                  }}>
                    <span style={{ color: C.muted, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 2 }}>{t('reference')}</span>
                    <span style={{ color: C.accent, fontSize: 15, fontWeight: 700 }}>#{voucher.references || voucher.id}</span>
                  </div>
                  <div style={{
                    background: C.cardInner, borderRadius: 10, padding: '8px 14px',
                    display: 'flex', flexDirection: 'column',
                  }}>
                    <span style={{ color: C.muted, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 2 }}>{t('valid_until')}</span>
                    <span style={{ color: expired ? C.danger : C.accentGold, fontSize: 15, fontWeight: 700 }}>
                      {fmtDate(voucher.expires_at)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Status do cliente */}
              <div style={{ marginTop: 14 }}>
                {voucher?.status === 'canceled' && <Badge bg={C.danger}>{t('badge_canceled')}</Badge>}
                {voucher?.status !== 'canceled' && customerStatus === 'approved' && <Badge bg="#276749">{t('badge_approved')}</Badge>}
                {voucher?.status !== 'canceled' && customerStatus === 'refused'  && <Badge bg={C.danger}>{t('badge_refused')}</Badge>}
                {voucher?.status !== 'canceled' && customerStatus == null && !expired && <Badge bg="#1e3a5a">{t('badge_waiting')}</Badge>}
                {voucher?.status !== 'canceled' && customerStatus == null && expired  && <Badge bg={C.danger}>{t('badge_expired')}</Badge>}
              </div>

              {customerStatus === 'refused' && voucher.customer_refuse_reason && (
                <div style={{ marginTop: 12, padding: '10px 14px', background: '#3d1a1a', borderRadius: 10 }}>
                  <Label>{t('refuse_reason_label')}</Label>
                  <Value style={{ fontSize: 13 }}>{voucher.customer_refuse_reason}</Value>
                </div>
              )}
            </div>

            {/* ── GRID DUAS COLUNAS ── */}
            <div className="presale-grid">

              {/* ── COLUNA ESQUERDA: itens do orçamento ── */}
              <div className="presale-left">
                {budgets.length > 0 && (
                  <div>
                    <div style={{ color: C.label, fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 12, paddingLeft: 4 }}>
                      {budgets.length} {budgets.length === 1 ? t('service') : t('services')} {t('in_the_budget')}
                    </div>
                    {budgets.map((b, idx) => (
                      <TourItemCard key={idx} b={b} idx={idx} presaleId={sale_id} accessToken={token} promo={bfOn ? bfPromo : null} />
                    ))}
                  </div>
                )}
              </div>

              {/* ── COLUNA DIREITA: resumo + ações ── */}
              <div className="presale-right">
                <div className="presale-right-payment">

                {/* Resumo total — dois cards PIX / Cartão (só PT) */}
                {canAct && totalPixCalc > 0 && plang === 'pt' ? (() => {
                  const pixTotal  = Math.round(totalPixCalc  * 100) / 100;
                  const cardTotal = Math.round(totalCardCalc * 100) / 100;
                  const maxFreeR  = paySettings?.installments?.max_no_interest_installments ?? 1;
                  const maxInstR  = paySettings?.installments?.max_installments ?? 12;
                  const rateR     = paySettings?.installments?.interest_rate ?? 1;
                  const minValR   = paySettings?.installments?.min_installment_value ?? 5;
                  const fmtR = (v) => `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
                  const round2 = (v) => Math.round(v * 100) / 100;
                  const calcInst = (total, n) => n <= maxFreeR ? total / n : total * Math.pow(1 + rateR / 100, n) / n;

                  // Parcelas disponíveis para cartão
                  const instOpts = [];
                  for (let n = 1; n <= maxInstR; n++) {
                    const val = calcInst(cardTotal, n);
                    if (val < minValR) break;
                    instOpts.push({ n, val: round2(val), hasInterest: n > maxFreeR });
                  }

                  // Valor da entrada PIX
                  const pixCharge = Math.round(pixTotal * rightPixSplit / 100 * 100) / 100;
                  const pixRest   = Math.round((pixTotal - pixCharge) * 100) / 100;

                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>

                      {/* ══ PIX ══ */}
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 7 }}>
                          <span style={{ fontSize: 15 }}>⚡</span>
                          <span style={{ color: '#68D391', fontWeight: 700, fontSize: 12, letterSpacing: 0.5, textTransform: 'uppercase' }}>PIX</span>
                          <span style={{ color: C.muted, fontSize: 10, marginLeft: 2 }}>{t('immediate_confirmation')}</span>
                        </div>
                        {[
                          { pct: 30, badge: t('most_chosen'), title: t('secure_spot_with') },
                          { pct: 50, badge: null,             title: t('down_50') },
                          { pct: 100, badge: null,            title: t('full_payment') },
                        ].filter(({ pct }) => !hasMonteluzCurrentMonth || pct === 100)
                        .map(({ pct, badge, title }) => {
                          const nowAmt  = round2(pixTotal * pct / 100);
                          const restAmt = round2(pixTotal - nowAmt);
                          const active  = rightPixSplit === pct;
                          if (nowAmt < minValR && pct < 100) return null;
                          if (!active) return (
                            <div key={pct} onClick={() => { setRightPixSplit(pct); setRightMethod('pix'); }} style={{
                              borderRadius: 10, padding: '8px 12px', cursor: 'pointer', marginBottom: 5,
                              border: `1.5px solid ${C.sep}`, background: C.cardInner,
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            }}>
                              <div>
                                <div style={{ color: C.value, fontWeight: 600, fontSize: 12 }}>{title}</div>
                                <div style={{ color: C.muted, fontSize: 10, marginTop: 1 }}>
                                  {pct < 100 ? t('balance_48h_short') : t('confirmed_immediately')}
                                </div>
                              </div>
                              <div style={{ textAlign: 'right', minWidth: 80 }}>
                                <div style={{ color: C.value, fontWeight: 800, fontSize: 13 }}>{fmtR(nowAmt)}</div>
                                {pct < 100
                                  ? <div style={{ color: '#68D391', fontSize: 10, marginTop: 1 }}>+ {fmtR(restAmt)}</div>
                                  : <div style={{ color: C.muted, fontSize: 10, marginTop: 1 }}>{t('nothing_more')}</div>}
                              </div>
                            </div>
                          );
                          return (
                            <div key={pct} style={{ borderRadius: 12, padding: '10px 12px', marginBottom: 5, border: '2px solid #68D391', background: '#071e14' }}>
                              {badge && (
                                <span style={{ background: '#68D391', color: '#052e16', borderRadius: 20, padding: '2px 9px', fontSize: 10, fontWeight: 700, marginBottom: 5, display: 'inline-block' }}>
                                  {badge}
                                </span>
                              )}
                              <div style={{ color: C.label, fontSize: 11, marginBottom: 1 }}>{title}</div>
                              <div style={{ color: '#68D391', fontWeight: 900, fontSize: 22, marginBottom: 2 }}>{fmtR(nowAmt)}</div>
                              <div style={{ color: C.muted, fontSize: 10, marginBottom: pct < 100 ? 7 : 0 }}>{t('today_confirmed')}</div>
                              {pct < 100 && (
                                <div style={{ background: '#0d2a1e', borderRadius: 7, padding: '6px 10px', display: 'flex', justifyContent: 'space-between' }}>
                                  <div>
                                    <div style={{ color: C.muted, fontSize: 10 }}>{t('remaining')}</div>
                                    <div style={{ color: C.value, fontWeight: 700, fontSize: 13 }}>{fmtR(restAmt)}</div>
                                  </div>
                                  <div style={{ textAlign: 'right' }}>
                                    <div style={{ color: C.muted, fontSize: 10 }}>{t('paid_48h_before')}</div>
                                    <div style={{ color: C.muted, fontSize: 10 }}>{t('of_boarding')}</div>
                                  </div>
                                </div>
                              )}
                              {totalBruto > pixTotal && (
                                <div style={{ marginTop: 6, background: '#0a2819', borderRadius: 7, padding: '6px 10px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                    <span style={{ color: '#FACC15', fontSize: 12, fontWeight: 600, textDecoration: 'line-through', textDecorationThickness: 1, textDecorationColor: 'rgba(250,204,21,.45)' }}>{t('of')} {fmtR(totalBruto)}</span>
                                    <span style={{ background: '#FACC15', color: '#412402', borderRadius: 20, padding: '0px 6px', fontSize: 9, fontWeight: 700 }}>-{pixBadgePct ?? Math.round((1 - pixTotal / totalBruto) * 100)}%</span>
                                    <span style={{ color: '#68D391', fontWeight: 900, fontSize: 13 }}>{t('per')} {fmtR(pixTotal)} {t('in_pix')}</span>
                                  </div>
                                  <div style={{ color: '#68D391', fontSize: 10, fontWeight: 600, marginTop: 1 }}>
                                    {t('you_save')} {fmtR(round2(totalBruto - pixTotal))}{transferNote}
                                  </div>
                                </div>
                              )}
                              {managerDiscount > 0 && (
                                <div style={{ marginTop: 4, background: '#1a0d2e', borderRadius: 7, padding: '5px 10px', color: '#c4b5fd', fontSize: 10, fontWeight: 600 }}>
                                  {t('manager_discount_applied_pre')} {voucher?.approval_type === 'discount_pct' ? `${voucher.approval_manager_value}%` : ''} {fmtR(managerDiscount)} {t('manager_discount_applied_post')}{totalBrutoTours < totalBruto ? ` — ${t('on_tours_only')}` : ''}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        <div style={{ textAlign: 'center', color: C.muted, fontSize: 10 }}>
                          {t('trust_line')}
                        </div>
                      </div>

                      {/* ══ Cartão de crédito — oculto para Monteluz ══ */}
                      {!hasMonteluzCurrentMonth && <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                          <span style={{ fontSize: 15 }}>💳</span>
                          <span style={{ color: C.accent, fontWeight: 700, fontSize: 12, letterSpacing: 0.5, textTransform: 'uppercase' }}>{t('credit_card_title')}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                          <div style={{ color: C.label, fontSize: 10, fontWeight: 700, letterSpacing: 1.1, textTransform: 'uppercase' }}>{t('installments_title')}</div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: C.label, fontSize: 10 }}>
                              <span style={{ width: 6, height: 6, borderRadius: 3, background: '#68D391', display: 'inline-block' }} /> {t('no_interest')}
                            </span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: C.label, fontSize: 10 }}>
                              <span style={{ width: 6, height: 6, borderRadius: 3, background: '#F6AD55', display: 'inline-block' }} /> {t('with_interest')}
                            </span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
                          {instOpts.map((opt) => {
                            const sel = rightInstallments === opt.n;
                            const grn = !opt.hasInterest;
                            return (
                              <button key={opt.n} onClick={() => { setRightInstallments(opt.n); setRightMethod('credit_card'); }} style={{
                                borderRadius: 7, padding: '5px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                border: `1.5px solid ${sel ? (grn ? '#68D391' : '#F6AD55') : (grn ? 'rgba(104,211,145,0.35)' : 'rgba(246,173,85,0.30)')}`,
                                background: sel ? (grn ? 'rgba(104,211,145,0.18)' : 'rgba(246,173,85,0.18)') : (grn ? 'rgba(104,211,145,0.06)' : 'rgba(246,173,85,0.05)'),
                                color: sel ? '#fff' : (grn ? '#86efac' : '#fcd34d'),
                              }}>{opt.n}x</button>
                            );
                          })}
                        </div>
                        <div style={{
                          borderRadius: 8, padding: '7px 12px', textAlign: 'center',
                          border: `1px solid ${rightInstallments <= maxFreeR ? 'rgba(104,211,145,0.25)' : 'rgba(246,173,85,0.30)'}`,
                          background: rightInstallments <= maxFreeR ? 'rgba(104,211,145,0.08)' : 'rgba(246,173,85,0.08)',
                          color: rightInstallments <= maxFreeR ? '#68D391' : '#F6AD55',
                          fontWeight: 700, fontSize: 13,
                        }}>
                          {rightInstallments === 1
                            ? `${fmtR(cardTotal)} ${t('cash')} · ${t('no_interest')}`
                            : rightInstallments <= maxFreeR
                              ? `${rightInstallments}${t('x_of')} ${fmtR(round2(calcInst(cardTotal, rightInstallments)))} · ${t('no_interest')}`
                              : `${rightInstallments}${t('x_of')} ${fmtR(round2(calcInst(cardTotal, rightInstallments)))} · ${t('total_word')} ${fmtR(round2(calcInst(cardTotal, rightInstallments) * rightInstallments))}`}
                        </div>
                        {totalBruto > cardTotal && (
                          <div style={{ marginTop: 6, background: '#0d1e2a', borderRadius: 7, padding: '6px 10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                              <span style={{ color: '#FACC15', fontSize: 12, fontWeight: 600, textDecoration: 'line-through', textDecorationThickness: 1, textDecorationColor: 'rgba(250,204,21,.45)' }}>{t('of')} {fmtR(totalBruto)}</span>
                              <span style={{ background: '#FACC15', color: '#412402', borderRadius: 20, padding: '0px 6px', fontSize: 9, fontWeight: 700 }}>-{cardBadgePct ?? Math.round((1 - cardTotal / totalBruto) * 100)}%</span>
                              <span style={{ color: C.accent, fontWeight: 900, fontSize: 13 }}>{t('per')} {fmtR(cardTotal)} {t('in_card')}</span>
                            </div>
                            <div style={{ color: C.accent, fontSize: 10, fontWeight: 600, marginTop: 1 }}>
                              {t('card_you_save')} {fmtR(round2(totalBruto - cardTotal))}{transferNote}
                            </div>
                            {managerDiscount > 0 && (
                              <div style={{ marginTop: 5, background: '#3b1a6e', borderRadius: 5, padding: '4px 8px', fontSize: 10, color: '#e9d5ff', fontWeight: 600, textAlign: 'center' }}>
                                🎁 {t('manager_discount_applied_pre')} {voucher?.approval_type === 'discount_pct' ? `${voucher.approval_manager_value}% ` : ''}{fmtR(managerDiscount)} {t('manager_discount_applied_post')}{totalBrutoTours < totalBruto ? ` — ${t('on_tours_only')}` : ''}
                              </div>
                            )}
                          </div>
                        )}
                      </div>}

                    </div>
                  );
                })() : (
                  <Card style={{ background: 'linear-gradient(135deg, #0f2a45 0%, #132035 100%)', border: `1px solid ${C.sep}` }}>
                    <SectionTitle icon="💰">{t('budget_summary')}</SectionTitle>
                    <Row label={`${budgets.length} ${budgets.length !== 1 ? t('services') : t('service')}`}
                         value={fmtMoney(totalBruto)} />

                    {/* Breakdown de pagamento quando já pago/parcial */}
                    {voucher.payment_status && voucher.payment_status !== 'pending' && (() => {
                      const isPix   = voucher.payment_method === 'pix';
                      const isCard  = voucher.payment_method === 'credit_card';
                      const pct     = voucher.payment_partial_pct || 100;
                      const pixNet  = Math.round(totalPixCalc * 100) / 100;
                      const cardNet = Math.round(totalCardCalc * 100) / 100;
                      const paid    = isPix ? Math.round(pixNet * pct / 100 * 100) / 100 : cardNet;
                      const rest    = isPix ? Math.round((pixNet - paid) * 100) / 100 : 0;
                      const inst    = voucher.payment_installments || 1;
                      return (
                        <div style={{ marginTop: 10, borderTop: `1px solid ${C.sep}`, paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {isPix && pct < 100 && (
                            <>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ color: C.label, fontSize: 13 }}>{t('pix_down')} ({pct}%)</span>
                                <span style={{ color: '#68D391', fontWeight: 700, fontSize: 14 }}>{fmtMoney(paid)}</span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ color: C.label, fontSize: 13 }}>{t('remaining_hour')}</span>
                                <span style={{ color: C.accentGold, fontWeight: 700, fontSize: 14 }}>{fmtMoney(rest)}</span>
                              </div>
                              <div style={{ background: '#1a2a1a', borderRadius: 8, padding: '8px 12px', color: C.accentGold, fontSize: 11, fontWeight: 600 }}>
                                {t('pay_balance_48h')}
                              </div>
                            </>
                          )}
                          {isPix && pct === 100 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ color: C.label, fontSize: 13 }}>{t('pix_paid')}</span>
                              <span style={{ color: '#68D391', fontWeight: 700, fontSize: 14 }}>{fmtMoney(paid)}</span>
                            </div>
                          )}
                          {isCard && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ color: C.label, fontSize: 13 }}>{t('card_paid_prefix')} {inst > 1 ? `${inst}x` : t('cash')}</span>
                              <span style={{ color: '#68D391', fontWeight: 700, fontSize: 14 }}>{fmtMoney(paid)}</span>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Fallback quando ainda não pagou */}
                    {(!voucher.payment_status || voucher.payment_status === 'pending') && (
                      <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        paddingTop: 14, borderTop: `2px solid ${C.sep}`, marginTop: 8,
                      }}>
                        <span style={{ color: C.value, fontWeight: 700, fontSize: 14 }}>{t('total_to_pay')}</span>
                        <span style={{ color: C.value, fontWeight: 900, fontSize: 20 }}>{fmtMoney(totalBruto)}</span>
                      </div>
                    )}

                    {/* ── Conversão internacional inline (EN/ES) ── */}
                    {canAct && plang !== 'pt' && (
                      <div style={{ marginTop: 14, borderTop: `1px solid ${C.sep}`, paddingTop: 14 }}>

                        {/* EN: USD fixo */}
                        {plang === 'en' && (
                          <div style={{ textAlign: 'center', minHeight: 48 }}>
                            {intlRateLoading && <CircularProgress size={22} style={{ color: C.accent }} />}
                            {!intlRateLoading && intlAmount && (
                              <>
                                <div style={{ color: C.muted, fontSize: 11, marginBottom: 3 }}>{t('pay_currency_label')}</div>
                                <div style={{ color: C.accent, fontWeight: 800, fontSize: 22 }}>
                                  🇺🇸 USD {Number(intlAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                                {intlRate && (
                                  <div style={{ color: C.muted, fontSize: 11, marginTop: 3 }}>
                                    1 USD = R$ {Number(intlRate).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </div>
                                )}
                                {managerDiscount > 0 && (
                                  <div style={{ marginTop: 6, background: '#1a0d2e', borderRadius: 7, padding: '4px 10px', color: '#c4b5fd', fontSize: 11, fontWeight: 600, display: 'inline-block' }}>
                                    🎁 Manager discount {voucher?.approval_type === 'discount_pct' ? `${voucher.approval_manager_value}% — ` : ''}R$ {managerDiscount.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} already applied
                                  </div>
                                )}
                              </>
                            )}
                            {!intlRateLoading && intlRateError && (
                              <div style={{ color: C.danger, fontSize: 12 }}>
                                ⚠ {t('rate_error') || 'Rate unavailable — try refreshing'}
                                <button onClick={() => { setIntlRateError(false); setIntlRetryKey(k => k + 1); }} style={{ marginLeft: 8, fontSize: 11, color: C.accent, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Retry</button>
                              </div>
                            )}
                          </div>
                        )}

                        {/* ES: seletor LatAm */}
                        {plang === 'es' && (
                          <div>
                            <div style={{ color: C.label, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
                              {t('pay_currency_label')}
                            </div>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                              {[
                                { code: 'ARS', flag: '🇦🇷', label: 'ARS' },
                                { code: 'CLP', flag: '🇨🇱', label: 'CLP' },
                                { code: 'COP', flag: '🇨🇴', label: 'COP' },
                                { code: 'MXN', flag: '🇲🇽', label: 'MXN' },
                              ].map(({ code, flag, label }) => {
                                const active = intlCurrency === code;
                                return (
                                  <button key={code} onClick={() => setIntlCurrency(code)} style={{
                                    borderRadius: 8, padding: '7px 12px', cursor: 'pointer',
                                    border: `1.5px solid ${active ? C.accent : C.sep}`,
                                    background: active ? '#0d1e2a' : C.cardInner,
                                    color: active ? C.accent : C.value,
                                    fontSize: 12, fontWeight: active ? 700 : 400,
                                    display: 'flex', alignItems: 'center', gap: 5,
                                  }}>
                                    {flag} {label}
                                  </button>
                                );
                              })}
                            </div>
                            {intlCurrency && (
                              <div style={{ textAlign: 'center', minHeight: 36 }}>
                                {intlRateLoading && <CircularProgress size={22} style={{ color: C.accent }} />}
                                {!intlRateLoading && intlAmount && (
                                  <>
                                    <div style={{ color: C.accent, fontWeight: 800, fontSize: 22 }}>
                                      {intlCurrency} {Number(intlAmount).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                    </div>
                                    {intlRate && (
                                      <div style={{ color: C.muted, fontSize: 11, marginTop: 3 }}>
                                        1 {intlCurrency} = R$ {Number(intlRate).toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                                      </div>
                                    )}
                                    {managerDiscount > 0 && (
                                      <div style={{ marginTop: 6, background: '#1a0d2e', borderRadius: 7, padding: '4px 10px', color: '#c4b5fd', fontSize: 11, fontWeight: 600, display: 'inline-block' }}>
                                        🎁 Descuento gerencial {voucher?.approval_type === 'discount_pct' ? `${voucher.approval_manager_value}% — ` : ''}R$ {managerDiscount.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ya aplicado
                                      </div>
                                    )}
                                  </>
                                )}
                                {!intlRateLoading && intlRateError && (
                                  <div style={{ color: C.danger, fontSize: 12 }}>
                                    ⚠ {t('rate_error') || 'Cotización no disponible. Intenta de nuevo.'}
                                    <button onClick={() => { setIntlRateError(false); setIntlRetryKey(k => k + 1); }} style={{ marginLeft: 8, fontSize: 11, color: C.accent, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Reintentar</button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </Card>
                )}

                {/* Validade */}
                {!expired && customerStatus == null && voucher?.status !== 'canceled' && (
                  <div style={{
                    textAlign: 'center',
                    padding: '7px 12px',
                    background: '#1a2d1a',
                    borderRadius: 10,
                    marginBottom: 8,
                    color: C.accentGold,
                    fontSize: 11,
                    fontWeight: 600,
                  }}>
                    {t('valid_until_pre')} {fmtDate(voucher.expires_at)} {t('valid_until_post')}
                  </div>
                )}

                {/* Ação: aprovar / recusar */}
                {canAct && (
                  <Card>
                    <SectionTitle>{t('your_answer')}</SectionTitle>

                    {!showRefuse ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <button
                          onClick={handleOpenPayModal}
                          disabled={actionLoading || (plang === 'es' && !intlCurrency) || (plang !== 'pt' && intlRateLoading)}
                          style={{
                            width: '100%',
                            background: C.approveGrad,
                            color: '#fff',
                            border: 'none',
                            borderRadius: 10,
                            padding: '12px 12px',
                            fontWeight: 800,
                            fontSize: 14,
                            cursor: (actionLoading || (plang === 'es' && !intlCurrency) || (plang !== 'pt' && intlRateLoading)) ? 'not-allowed' : 'pointer',
                            opacity: (actionLoading || (plang !== 'pt' && intlRateLoading)) ? 0.7 : 1,
                            minHeight: 44,
                            WebkitTapHighlightColor: 'transparent',
                            letterSpacing: 0.3,
                          }}
                        >
                          {plang === 'en'
                            ? intlRateLoading
                              ? '...'
                              : intlAmount
                                ? `Pay USD ${Number(intlAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} →`
                                : 'Pay →'
                            : plang === 'es'
                              ? !intlCurrency
                                ? 'Pagar →'
                                : intlRateLoading
                                  ? 'Cargando...'
                                  : intlAmount
                                    ? `Pagar ${intlCurrency} ${Number(intlAmount).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} →`
                                    : 'Pagar →'
                              : rightMethod === 'pix'
                                ? `${t('generate_pix')} R$ ${(Math.round(totalPixCalc * rightPixSplit / 100 * 100) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                                : t('pay_with_card')}
                        </button>
                        <button
                          onClick={() => setShowRefuse(true)}
                          disabled={actionLoading}
                          style={{
                            width: '100%',
                            background: 'transparent',
                            color: C.danger,
                            border: `1.5px solid ${C.danger}`,
                            borderRadius: 10,
                            padding: '10px 12px',
                            fontWeight: 700,
                            fontSize: 13,
                            cursor: actionLoading ? 'not-allowed' : 'pointer',
                            opacity: actionLoading ? 0.7 : 1,
                            WebkitTapHighlightColor: 'transparent',
                          }}
                        >
                          {t('refuse')}
                        </button>
                      </div>
                    ) : (
                      <div>
                        <p style={{ color: C.label, fontSize: 13, marginBottom: 10, lineHeight: 1.5 }}>
                          {t('refuse_intro')}
                        </p>
                        <textarea
                          value={refuseReason}
                          onChange={(e) => setRefuseReason(e.target.value)}
                          rows={3}
                          placeholder={t('refuse_placeholder')}
                          style={{
                            width: '100%', boxSizing: 'border-box',
                            background: C.cardInner,
                            border: `1px solid ${C.sep}`,
                            borderRadius: 10,
                            color: C.value,
                            fontSize: 15,
                            padding: '12px 14px',
                            resize: 'vertical',
                            marginBottom: 12,
                            outline: 'none',
                          }}
                        />
                        <div style={{ display: 'flex', gap: 10 }}>
                          <button
                            onClick={handleRefuse}
                            disabled={actionLoading}
                            style={{
                              flex: 1,
                              background: C.refuseGrad,
                              color: '#fff',
                              border: 'none',
                              borderRadius: 10,
                              padding: '14px 0',
                              fontWeight: 700,
                              fontSize: 14,
                              cursor: actionLoading ? 'not-allowed' : 'pointer',
                              opacity: actionLoading ? 0.7 : 1,
                              minHeight: 48,
                            }}
                          >
                            {actionLoading ? '...' : t('confirm_refuse')}
                          </button>
                          <button
                            onClick={() => setShowRefuse(false)}
                            style={{
                              flex: 1,
                              background: 'transparent',
                              color: C.label,
                              border: `1px solid ${C.sep}`,
                              borderRadius: 10,
                              padding: '14px 0',
                              fontWeight: 600,
                              fontSize: 14,
                              cursor: 'pointer',
                              minHeight: 48,
                            }}
                          >
                            {t('back_simple')}
                          </button>
                        </div>
                      </div>
                    )}
                  </Card>
                )}

                {/* Resposta já dada */}
                {customerStatus === 'approved' && (
                  <div style={{
                    textAlign: 'center', padding: '20px 16px',
                    background: '#1a3d2a', borderRadius: 16, marginBottom: 16,
                  }}>
                    <div style={{ fontSize: 36, marginBottom: 8 }}>🎉</div>
                    <div style={{ color: '#68D391', fontWeight: 700, fontSize: 16 }}>{t('approved_title')}</div>
                    <div style={{ color: C.label, fontSize: 13, marginTop: 6 }}>
                      {t('approved_text')}
                    </div>
                  </div>
                )}

                </div>{/* fim presale-right-payment */}

                {/* Comentários */}
                <div className="presale-right-chat">
                <CommentsSection
                  saleId={sale_id}
                  token={token}
                  comments={comments}
                  fetchComments={fetchComments}
                  prefilledMessage={chatPreFill}
                  onPrefilledUsed={() => setChatPreFill(null)}
                  sectionRef={chatRef}
                />
                </div>
              </div>
              {/* fim col-right */}
            </div>
            {/* fim grid */}

            {/* ── TERMOS DO ORÇAMENTO (terms_of_use type='budget', por idioma) ── */}
            {(voucher?.terms?.description || voucher?.terms?.description2 || voucher?.terms?.description3) && (
              <Card style={{ border: `1px solid ${C.sep}`, marginTop: 0, marginBottom: 16 }}>
                <div style={{ color: C.title, fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>
                  {t('terms_title')}
                </div>
                {['description', 'description2', 'description3'].map((f) => voucher?.terms?.[f] ? (
                  <div
                    key={f}
                    style={{ color: C.label, fontSize: 12, lineHeight: 1.7, wordBreak: 'break-word' }}
                    dangerouslySetInnerHTML={{ __html: String(voucher.terms[f])
                      .replace(/color\s*:\s*[^;}"']+;?/gi, '')
                      .replace(/<a /gi, '<a style="color:#62DEC2;" ') }}
                  />
                ) : null)}
              </Card>
            )}

            {/* ── RODAPÉ FULL TOUR (full width) ── */}
            <Card style={{
              background: 'linear-gradient(135deg, #0f2040 0%, #0a1628 100%)',
              border: `1px solid ${C.sep}`,
              textAlign: 'center',
              marginTop: 0,
            }}>
              {/* Logo / marca */}
              <div style={{ marginBottom: 12 }}>
                {logo
                  ? <img src={logo} alt="Full Tour" style={{ height: 44, maxWidth: 160, objectFit: 'contain' }} />
                  : <span style={{ color: '#62DEC2', fontWeight: 900, fontSize: 28, letterSpacing: 2 }}>Full Tour</span>
                }
              </div>

              <div style={{ color: C.label, fontSize: 13, lineHeight: 1.8, marginBottom: 14 }}>
                {t('footer_about')}
              </div>

              <Divider />

              {/* Apps */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ color: C.label, fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 12 }}>
                  {t('download_app')}
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
                  {/* iOS */}
                  <a
                    href="https://apps.apple.com/app/full-tour/id6742218087"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none' }}
                  >
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      background: '#000', borderRadius: 10,
                      padding: '9px 18px', border: '1px solid #444',
                      minWidth: 145,
                    }}>
                      <svg width="22" height="22" viewBox="0 0 814 1000" fill="white" xmlns="http://www.w3.org/2000/svg">
                        <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-37.5-155.5-127.4C46.2 842.1 0 727.3 0 617.8 0 405.4 140.5 280.7 279.4 280.7c70.6 0 129.2 46.4 173.1 46.4 42.1 0 108.6-49 191.2-49 30.8 0 110.8 2.9 168.3 73.4zm-234.6-194c30.1-36 53.4-85.8 53.4-135.6 0-6.5-.6-13-1.9-18.2-50.5 1.9-110.7 33.7-147.4 75.3-28.4 32.4-55.1 82.2-55.1 132.6 0 7.1 1.3 14.2 1.9 16.5 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.9z"/>
                      </svg>
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ color: '#aaa', fontSize: 9, letterSpacing: 0.5 }}>{t('available_on_ios')}</div>
                        <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>App Store</div>
                      </div>
                    </div>
                  </a>
                  {/* Android */}
                  <a
                    href="https://play.google.com/store/apps/details?id=com.fulltour.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none' }}
                  >
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      background: '#000', borderRadius: 10,
                      padding: '9px 18px', border: '1px solid #444',
                      minWidth: 145,
                    }}>
                      <svg width="22" height="22" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                        <path fill="#4CAF50" d="M12 2.9 29.4 20 12 37.1A4 4 0 0 1 10 34V6a4 4 0 0 1 2-3.1z"/>
                        <path fill="#F44336" d="m32.7 22.9 4.5-2.6a2 2 0 0 0 0-3.5l-4.5-2.6L28 20l4.7 2.9z"/>
                        <path fill="#FFC107" d="m12 2.9 18.4 10.8L26 18.4 14.7 7.1A4 4 0 0 0 12 2.9z"/>
                        <path fill="#2196F3" d="m12 37.1 18.4-10.7-4.4-4.5L14.7 33A4 4 0 0 1 12 37.1z"/>
                      </svg>
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ color: '#aaa', fontSize: 9, letterSpacing: 0.5 }}>{t('available_on_android')}</div>
                        <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>Google Play</div>
                      </div>
                    </div>
                  </a>
                </div>
              </div>

              <Divider />

              {/* Rodapé final */}
              <div style={{ color: C.muted, fontSize: 11, lineHeight: 1.8 }}>
                {t('footer_personal')} {voucher.references}
                <br />
                Full Tour Technology · +56 9 6736 7782 · Marcelo Ferreira · Sistema v2.0.0
              </div>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default connect()(PresalePage);
